/**
 * Enjoy.jsx — /enjoy live events + Signature Experiences portal.
 *
 * Founder ruling 14 Feb 2026:
 *   - /enjoy is OPEN — same as /table. Only /club has a signup gate.
 *   - Header/footer parity with /table: same chrome, same wordmark
 *     pattern (`/enjoy · by Concierge®`), no MindEscapes wrapper.
 *   - Card-in-grid carries the full event shape (title · venue · date
 *     · whisper · price · category) and a hero image where one is
 *     available. Where no overlay image is set, a Google-Places-
 *     enriched photo is surfaced lazily by the backend.
 *
 * Per FIR-20260612-012 · /enjoy Build Brief v1.0 + Addendum A.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import {
  Music, Mic2, Disc3, Smile, Drama, Trophy, Sparkle, Sparkles,
  Users, Baby, Palette, Image as ImageIcon, Coffee, Utensils,
  Wrench, Mountain, Award, Flame, MicVocal, Film, Star,
} from "lucide-react";
import { TableHeader } from "@/components/table/TableHeader";
import { TableFooter } from "@/components/table/TableFooter";
import TopCitiesGrid, { PICKS_ENJOY } from "@/components/global/TopCitiesGrid";
import { TableWhatsAppPill } from "@/pages/Table";
import * as enjoy from "./enjoy/api";
import * as clubApi from "@/pages/club/api";
import { DrawerVoiceLine } from "@/components/shared/DrawerAtmosphere";
import "@/styles/table-surface.css";
import "@/styles/enjoy.css";

import MemberContextBand from "../components/MemberContextBand";
import CldImg from "../components/CldImg";
// Lucide icon resolver · backend returns icon name string, we map to component.
const ICON_MAP = {
  Music, Mic2, Disc3, Smile, Drama, Trophy, Sparkle, Sparkles,
  Users, Baby, Palette, Image: ImageIcon, Coffee, Utensils,
  Wrench, Mountain, Award, Flame, MicVocal, Film, Star,
};

/* ── FIR-20260217-TMSG · Fire-and-forget telemetry beacons ─────────────
 * Uses navigator.sendBeacon when available (survives page unload) and
 * falls back to fetch({keepalive:true}). Never blocks user action,
 * never throws. Backend writes to lc_enjoy_telemetry (capped 100MB).
 *
 * Impressions are deduped per session per event_id via a Set on the
 * window (so it survives within an SPA session but not across reloads).
 */
const _ENJOY_BACKEND = (typeof process !== "undefined"
  && process.env && process.env.REACT_APP_BACKEND_URL) || "";

function _enjoyImpressionMemo() {
  if (typeof window === "undefined") return new Set();
  if (!window.__enjoy_impressions) window.__enjoy_impressions = new Set();
  return window.__enjoy_impressions;
}

function enjoyBeacon(pathSuffix, payload) {
  try {
    if (!_ENJOY_BACKEND) return;
    const url = `${_ENJOY_BACKEND}/api/enjoy/telemetry/${pathSuffix}`;
    const body = JSON.stringify(payload || {});
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "include",
    }).catch(() => {});
  } catch (_) {
    // Telemetry must never affect the user surface.
  }
}

function enjoyFireImpression(event) {
  if (!event || !event.event_id) return;
  const memo = _enjoyImpressionMemo();
  const k = `${event.source || "?"}::${event.event_id}`;
  if (memo.has(k)) return;
  memo.add(k);
  enjoyBeacon("impression", {
    event_id: String(event.event_id),
    source: event.source || "unknown",
    sale_status: event.sale_status || null,
    bucket: (event.booking && event.booking.status_gate && event.booking.status_gate.bucket) || null,
  });
}

function enjoyResolveCtaKind(event, ctaIntent) {
  // ctaIntent: "box_office" | "concierge_request" | "drawer_open"
  if (ctaIntent !== "box_office") return ctaIntent;
  const notes = (event && event.booking && event.booking.notes) || [];
  if (Array.isArray(notes) && notes.includes("tier1_attraction_substitute")) {
    return "box_office_tier1";
  }
  return "box_office";
}

function enjoyFireClick(event, ctaIntent) {
  if (!event || !event.event_id) return;
  enjoyBeacon("click", {
    event_id: String(event.event_id),
    source: event.source || "unknown",
    sale_status: event.sale_status || null,
    bucket: (event.booking && event.booking.status_gate && event.booking.status_gate.bucket) || null,
    cta_kind: enjoyResolveCtaKind(event, ctaIntent),
  });
}
function CatIcon({ name, size = 14 }) {
  const Cmp = ICON_MAP[name] || Sparkles;
  return <Cmp size={size} strokeWidth={1.75} />;
}

// Fallback when /api/enjoy/categories hasn't responded yet · matches the
// universal anchors the backend always returns.
const FALLBACK_CATEGORIES = [
  { key: "all", label: "All", icon: "Sparkles", platforms: [] },
  { key: "music", label: "Music", icon: "Music", platforms: [] },
  { key: "theatre", label: "Theatre", icon: "Drama", platforms: [] },
  { key: "sports", label: "Sports", icon: "Trophy", platforms: [] },
  { key: "festivals", label: "Festivals", icon: "Sparkle", platforms: [] },
  { key: "comedy", label: "Comedy", icon: "Smile", platforms: [] },
  { key: "family", label: "Family", icon: "Users", platforms: [] },
  { key: "arts", label: "Arts", icon: "Palette", platforms: [] },
];

const SIGNATURE_TAB = { k: "_experience", l: "Signature Experiences" };
const CITY_FAVOURITES = ["London", "Mumbai", "Dubai", "New York"];
const WHEN_OPTIONS = [
  { k: "", l: "Anytime" },
  { k: "this_week", l: "This week" },
  { k: "this_month", l: "This month" },
];
const PRICE_BANDS = [
  { k: "", l: "Any price" },
  { k: "low", l: "Under 100" },
  { k: "mid", l: "100–300" },
  { k: "high", l: "300+" },
];

const MEMBER_LS_KEY = "club_member_session_v1";

function loadStoredMember() {
  try {
    const raw = window.localStorage.getItem(MEMBER_LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && v.card_no ? v : null;
  } catch { return null; }
}
function storeMember(m) {
  if (!m) return;
  try { window.localStorage.setItem(MEMBER_LS_KEY, JSON.stringify(m)); }
  catch (e) { /* localStorage unavailable */ }
}
function clearMember() {
  try { window.localStorage.removeItem(MEMBER_LS_KEY); }
  catch (e) { /* localStorage unavailable */ }
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtDateRange(startIso, endIso, dateNote, kind) {
  if (kind === "experience") {
    return dateNote || "By arrangement";
  }
  if (!startIso) return dateNote || "TBD";
  try {
    const s = new Date(startIso);
    const sd = s.getUTCDate();
    const sm = MONTHS_SHORT[s.getUTCMonth()];
    const sy = s.getUTCFullYear();
    if (endIso) {
      const e = new Date(endIso);
      const ed = e.getUTCDate();
      const em = MONTHS_SHORT[e.getUTCMonth()];
      const ey = e.getUTCFullYear();
      if (sy === ey && sm === em && sd === ed) {
        return `${sd} ${sm} ${sy}`;
      }
      if (sy === ey && sm === em) return `${sd}–${ed} ${sm} ${sy}`;
      if (sy === ey) return `${sd} ${sm} – ${ed} ${em} ${sy}`;
      return `${sd} ${sm} ${sy} – ${ed} ${em} ${ey}`;
    }
    return `${sd} ${sm} ${sy}`;
  } catch { return dateNote || "TBD"; }
}

function priceLine(event) {
  if (event.kind === "experience") return "On request";
  if (event.price_from) {
    const cur = event.currency || "";
    return `From ${cur} ${event.price_from}`.trim();
  }
  return "On request";
}

/* ── Route-trail copy · "verified Nh ago" · founder ear-test 14 Feb 2026 PM
 * Source-of-truth pin: this number ALWAYS comes from the resolver's
 * _URL_VERIFY_CACHE (the same cache that caught Yas Marina). Never
 * computed from render time. Empty when nothing's been verified. */
function verifiedTrail(verifiedAtEpoch) {
  if (!verifiedAtEpoch) return null;
  const nowSec = Date.now() / 1000;
  const diff = Math.max(0, nowSec - verifiedAtEpoch);
  if (diff < 60) return "verified just now";
  if (diff < 3600) return `verified ${Math.round(diff / 60)} min ago`;
  if (diff < 24 * 3600) return `verified ${Math.round(diff / 3600)} hr ago`;
  const days = Math.round(diff / (24 * 3600));
  return `verified ${days} day${days === 1 ? "" : "s"} ago`;
}


// Subtle tinted bands · same palette /table uses (TINTS) for monogram fallback.
const TINTS = [
  "var(--t-brass-wash)",
  "var(--t-slate-wash)",
  "var(--t-verified-wash)",
  "var(--t-pending-wash)",
];

/* ── Card ─────────────────────────────────────────────────────────────── */
/* ── Atmosphere chips · TM Discovery harvest ──────────────────────────── *
 * Founder ruling 14 Feb 2026 PM-3: surface every honest atmosphere field
 * TM gives us as a chip. Provenance = `source` (always implicit). No fact
 * fabrication — chips render ONLY when the underlying field is present.
 * Aligned with /table's accessibility/dietary chip rhythm.
 */
/* ── FIR-20260217-TMSG · sale-badge resolver · 17 Feb 2026 PM ───────
 * Original SALE_STATUS_STYLES had the canonical badge bug founder
 * called out: an `offsale` event was labelled "Not yet on sale" — the
 * EXACT opposite of the truth. The styles table now provides colour +
 * test-id only; the LABEL is resolved per-event via resolveSaleBadge()
 * which inspects sale_status + sale_starts_at + the 2025-01-01 sentinel
 * stale-event marker.
 *
 * Buckets (must match providers.py._gate_bucket on the backend):
 *   - onsale  + (no sale_starts_at OR sale_starts_at <= now)        → "On sale now"
 *   - onsale  + (sale_starts_at >  now AND sale_starts_at >= 2025-01-02) → "Not yet on sale"
 *   - offsale (any · incl. 2025-01-01 sentinel)                     → "Off sale"
 *   - sold_out                                                      → "Sold out"
 *   - cancelled                                                     → "Cancelled"
 *   - postponed                                                     → "Postponed"
 *   - rescheduled                                                   → "Rescheduled"
 */
const SALE_STATUS_STYLES = {
  onsale:      { bg: "#0f5c1f", color: "#fff", testid: "sale-onsale" },
  offsale:     { bg: "#8b6f3a", color: "#fff", testid: "sale-offsale" },
  sold_out:    { bg: "#a23b3b", color: "#fff", testid: "sale-soldout" },
  postponed:   { bg: "#8b6f3a", color: "#fff", testid: "sale-postponed" },
  cancelled:   { bg: "#1d1d1d", color: "#ddd", testid: "sale-cancelled" },
  rescheduled: { bg: "#8b6f3a", color: "#fff", testid: "sale-rescheduled" },
};

const _TM_SENTINEL_BEFORE = Date.UTC(2025, 0, 2, 0, 0, 0); // 2025-01-02T00:00:00Z

function resolveSaleBadge(event) {
  const status = (event && event.sale_status) || null;
  if (!status) return null;
  const style = SALE_STATUS_STYLES[status];
  if (!style) return null;

  let label = null;
  if (status === "onsale") {
    // True pre-sale: sale starts STRICTLY in the future AND is NOT the
    // 2025-01-01 sentinel (which would otherwise mis-classify stale
    // offsale events as "Not yet on sale" — the original bug).
    const starts = event.sale_starts_at ? Date.parse(event.sale_starts_at) : null;
    const now = Date.now();
    if (starts !== null && !Number.isNaN(starts) && starts >= _TM_SENTINEL_BEFORE && starts > now) {
      label = "Not yet on sale";
    } else {
      label = "On sale now";
    }
  } else if (status === "offsale") {
    // ANY offsale — including the 2025-01-01 sentinel — reads "Off sale".
    // Never "Not yet on sale", never "Sales open soon".
    label = "Off sale";
  } else if (status === "sold_out") {
    label = "Sold out";
  } else if (status === "cancelled") {
    label = "Cancelled";
  } else if (status === "postponed") {
    label = "Postponed";
  } else if (status === "rescheduled") {
    label = "Rescheduled";
  } else {
    return null;
  }

  return {
    label,
    bg: style.bg,
    color: style.color,
    testid: style.testid,
  };
}

function fmtSaleOpens(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    });
  } catch (e) { return iso; }
}

// Founder doctrine 15 Feb 2026 PM-8 · empty/null/whitespace all collapse
// to "absent". Belt-and-braces against the backend `(x or "").strip() or
// None` idiom — we never want a label header to render with a blank value.
function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

// Short tz label from an IANA name (e.g. "Europe/London" → "BST"/"GMT").
// Honest fallback: null when the input doesn't parse — never invents.
function shortTz(tz, when) {
  if (!hasValue(tz)) return null;
  try {
    const dt = when ? new Date(when) : new Date();
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz, timeZoneName: "short",
    }).formatToParts(dt);
    const found = parts.find((p) => p.type === "timeZoneName");
    return found ? found.value : null;
  } catch (e) { return null; }
}

// "On sale until 12 Aug" · short, factual. Founder doctrine: only when
// the real `endDateTime` exists and is in the future — never a cosmetic
// countdown. The future-guard runs server-side too (belt and braces).
function fmtSaleEnds(iso) {
  if (!hasValue(iso)) return null;
  try {
    const d = new Date(iso);
    if (!(d.getTime() > Date.now())) return null;  // past → suppressed
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric",
      year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    });
  } catch (e) { return null; }
}

// ── New TM-rich drawer blocks · founder ruling 16 Feb 2026 PM-10 ──
//    Three structured surfaces that turn the "too brief" drawer into a
//    fact-led card: source badges (Official · Ticketmaster + sale state),
//    event details block (Venue/Location/Date/Time/Performer/Genre), and
//    ticket-status block (sale state + sale-until + official route). All
//    three render ONLY when the underlying field is present; never invent.

// Format an event date as "3 July 2026" — no time, no tz.
function fmtEventDateLong(iso) {
  if (!hasValue(iso)) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch (e) { return null; }
}

// Pick the primary performer name from `attractions[]` · prefer the row
// whose name doesn't equal the event title verbatim (e.g. tribute acts
// where the title IS the act). Honest fallback: null.
function primaryPerformer(event) {
  const list = event && Array.isArray(event.attractions) ? event.attractions : [];
  if (!list.length) return null;
  // Pick the first attraction with a non-empty name distinct from the title.
  const distinct = list.find(
    (a) => hasValue(a && a.name) && a.name.trim() !== (event.title || "").trim()
  );
  return (distinct && distinct.name) || (list[0] && list[0].name) || null;
}

// Source label per provider · "Ticketmaster" / "Concierge® desk" / etc.
function sourceLabel(event) {
  if (!event) return null;
  if (event.source === "ticketmaster") return "Ticketmaster";
  if (event.source === "curated" || event.is_curated) return "Concierge® desk";
  if (event.source === "predicthq") return "PredictHQ";
  // Rule 14 · the SERP pipe is never named — cite the underlying listing source.
  if (event.source === "serp_events") return event.listing_source_domain || "Listed source";
  if (event.kind === "experience") return "Concierge® desk";
  return event.source ? event.source.replace(/_/g, " ") : null;
}

// Genre + sub-genre composed as "Music · Tribute" (or just "Music" when no
// sub). Returns null when neither side has a value.
function composedGenre(event) {
  if (!event) return null;
  const g = hasValue(event.genre) ? event.genre : null;
  const sg = hasValue(event.sub_genre) && event.sub_genre !== event.genre
    ? event.sub_genre : null;
  if (g && sg) return `${g} · ${sg}`;
  if (g) return g;
  if (sg) return sg;
  return null;
}

// ── <DrawerSourceBadges /> ──────────────────────────────────────────
// Sits directly under the title + provenance line. Two pill badges:
//   · "Official · Ticketmaster" (or "Curated by Concierge®" etc.)
//   · Sale status from TM (`On sale now` / `Sale opens…` / `Sold out`)
function DrawerSourceBadges({ event }) {
  if (!event) return null;
  const badges = [];

  // Source pill — always renders for any sourced event.
  const src = sourceLabel(event);
  if (src) {
    const isOfficial = event.source === "ticketmaster";
    const isCurated = event.source === "curated" || event.is_curated || event.kind === "experience";
    badges.push({
      key: "source",
      label: isOfficial ? `Official · ${src}` : isCurated ? "Curated by Concierge®" : `Per ${src}`,
      testid: "enjoy-drawer-badge-source",
      style: {
        background: "var(--enjoy-card-bg)",
        color: "var(--enjoy-ink)",
        border: "1px solid var(--enjoy-line)",
      },
    });
  }

  // Sale status pill — colour from SALE_STATUS_STYLES, label from
  // resolveSaleBadge() to apply the sentinel rule (FIR-20260217-TMSG).
  const _saleBadge = resolveSaleBadge(event);
  if (_saleBadge) {
    badges.push({
      key: "sale_status",
      label: _saleBadge.label,
      testid: `enjoy-drawer-badge-${_saleBadge.testid}`,
      style: { background: _saleBadge.bg, color: _saleBadge.color, border: "none" },
    });
  } else if (event.sale_starts_at) {
    badges.push({
      key: "sale_opens",
      label: "Sale opens soon",
      testid: "enjoy-drawer-badge-sale-opens",
      style: { background: "#8b6f3a", color: "#fff", border: "none" },
    });
  }

  if (badges.length === 0) return null;

  return (
    <div
      data-testid="enjoy-drawer-source-badges"
      style={{
        display: "flex", flexWrap: "wrap", gap: 8,
        marginTop: 12, marginBottom: 4,
      }}
    >
      {badges.map((b) => (
        <span
          key={b.key} data-testid={b.testid}
          className="t-mono uppercase"
          style={{
            display: "inline-flex", alignItems: "center",
            fontSize: 10.5, letterSpacing: "0.16em",
            padding: "5px 11px", borderRadius: 100,
            fontWeight: 600,
            ...b.style,
          }}
        >{b.label}</span>
      ))}
    </div>
  );
}

// ── <DrawerEventDetails /> ──────────────────────────────────────────
// Structured rows — Venue / Location / Date / Time / Performer / Genre /
// Source. Renders ONLY when the row has a real value. No "TBD" fillers.
function DrawerEventDetails({ event }) {
  if (!event) return null;
  const rows = [];

  if (hasValue(event.venue_name)) {
    rows.push({ key: "venue", label: "Venue", value: event.venue_name, testid: "enjoy-drawer-details-venue" });
  }
  const loc = [event.city, event.country].filter((v) => hasValue(v)).join(", ");
  if (loc) {
    rows.push({ key: "location", label: "Location", value: loc, testid: "enjoy-drawer-details-location" });
  }
  const dateStr = fmtEventDateLong(event.start_at);
  if (dateStr) {
    rows.push({ key: "date", label: "Date", value: dateStr, testid: "enjoy-drawer-details-date" });
  }
  if (hasValue(event.show_localtime)) {
    const tzAbbr = shortTz(event.tz, event.start_at);
    const timeStr = tzAbbr ? `${event.show_localtime} ${tzAbbr}` : event.show_localtime;
    rows.push({ key: "time", label: "Time", value: timeStr, testid: "enjoy-drawer-details-time" });
  }
  const performer = primaryPerformer(event);
  if (performer) {
    rows.push({ key: "performer", label: "Performer", value: performer, testid: "enjoy-drawer-details-performer" });
  }
  const g = composedGenre(event);
  if (g) {
    rows.push({ key: "genre", label: "Genre", value: g, testid: "enjoy-drawer-details-genre" });
  }
  const src = sourceLabel(event);
  if (src) {
    rows.push({ key: "source", label: "Source", value: src, testid: "enjoy-drawer-details-source" });
  }
  if (hasValue(event.price_from)) {
    const cur = event.currency || "";
    const range = hasValue(event.price_to) && event.price_to !== event.price_from
      ? `${cur} ${event.price_from} – ${cur} ${event.price_to}`.trim()
      : `${cur} ${event.price_from}`.trim();
    rows.push({ key: "price", label: hasValue(event.price_to) ? "Price" : "From", value: range, testid: "enjoy-drawer-details-price" });
  }

  if (rows.length === 0) return null;

  return (
    <section
      data-testid="enjoy-drawer-details"
      style={{
        marginTop: 18,
        padding: "14px 16px",
        background: "var(--enjoy-card-bg, var(--enjoy-paper))",
        border: "1px solid var(--enjoy-line)",
        borderRadius: 4,
      }}
    >
<p className="t-mono uppercase"
        style={{
          fontSize: 10, letterSpacing: "0.22em",
          color: "var(--enjoy-brass-deep)",
          fontWeight: 600, marginBottom: 12,
        }}
      >Event details</p>
      <dl style={{
        display: "grid", gridTemplateColumns: "92px 1fr",
        rowGap: 6, columnGap: 14, margin: 0,
      }}>
        {rows.map((r) => (
          <React.Fragment key={r.key}>
            <dt
              className="t-mono uppercase"
              style={{
                fontSize: 10, letterSpacing: "0.14em",
                color: "var(--enjoy-muted)", fontWeight: 600,
                paddingTop: 2,
              }}
            >{r.label}</dt>
            <dd
              data-testid={r.testid}
              style={{
                fontSize: 13.5, color: "var(--enjoy-ink)",
                margin: 0, lineHeight: 1.4,
              }}
            >{r.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

// ── <DrawerTicketStatus /> ──────────────────────────────────────────
// Three short lines: sale status · sale-until window · "Official
// ticketing route" (only when TM provides the URL — never invented).
function DrawerTicketStatus({ event }) {
  if (!event) return null;
  const isOfficial = event.source === "ticketmaster" && hasValue(event.direct_book_url);
  if (!isOfficial && !event.sale_status && !event.sale_ends_at && !event.sale_starts_at) {
    return null;
  }

  const _resolved = resolveSaleBadge(event);
  const saleLabel = _resolved ? _resolved.label
    : event.sale_starts_at ? "Sales open soon" : null;
  const saleEndsStr = fmtSaleEnds(event.sale_ends_at);
  const saleOpensStr = event.sale_starts_at ? fmtSaleOpens(event.sale_starts_at) : null;

  return (
    <section
      data-testid="enjoy-drawer-ticket-status"
      style={{
        marginTop: 12,
        padding: "12px 16px",
        background: event.sale_status === "onsale"
          ? "rgba(15, 92, 31, 0.05)"
          : "var(--enjoy-paper)",
        border: "1px solid var(--enjoy-line)",
        borderLeft: event.sale_status === "onsale"
          ? "3px solid #0f5c1f"
          : "3px solid var(--enjoy-brass)",
        borderRadius: 4,
      }}
    >
      <p className="t-mono uppercase"
        style={{
          fontSize: 10, letterSpacing: "0.22em",
          color: "var(--enjoy-brass-deep)",
          fontWeight: 600, marginBottom: 8,
        }}
      >Ticket status</p>
      {saleLabel && (
        <p
          data-testid="enjoy-drawer-ticket-status-label"
          style={{ fontSize: 14, color: "var(--enjoy-ink)", margin: "0 0 4px", fontWeight: 500 }}
        >{saleLabel}</p>
      )}
      {saleEndsStr && (
        <p
          data-testid="enjoy-drawer-ticket-status-ends"
          style={{ fontSize: 13, color: "var(--enjoy-muted)", margin: "0 0 4px" }}
        >On sale until {saleEndsStr}</p>
      )}
      {saleOpensStr && event.sale_status !== "onsale" && event.sale_status !== "offsale"
        && (() => {
          // FIR-20260217-TMSG · sentinel suppress · drawer honesty.
          // Don't render "Sale opens Jan 1, 2025" on a row that's
          // actually offsale or whose sale_starts_at is the TM
          // 2025-01-01 sentinel (= unknown/lost real date).
          const t = Date.parse(event.sale_starts_at);
          const SENTINEL = Date.UTC(2025, 0, 2);
          return !Number.isNaN(t) && t >= SENTINEL;
        })() && (
        <p
          data-testid="enjoy-drawer-ticket-status-opens"
          style={{ fontSize: 13, color: "var(--enjoy-muted)", margin: "0 0 4px" }}
        >Sale opens {saleOpensStr}</p>
      )}
      {isOfficial && (
        <p
          data-testid="enjoy-drawer-ticket-status-route"
          className="t-mono uppercase"
          style={{
            fontSize: 10.5, letterSpacing: "0.14em",
            color: "var(--enjoy-brass-deep)",
            margin: "6px 0 0", fontWeight: 600,
          }}
        >Official ticketing route</p>
      )}
    </section>
  );
}

function AtmosphereChips({ event }) {
  if (!event) return null;
  const chips = [];
  // Sale status, show time, sale ends, sale opens, genre, performer count
  // are now surfaced in DrawerEventDetails + DrawerTicketStatus + the
  // header badges. AtmosphereChips ("Good to know") keeps the residual
  // ATTRIBUTES that don't belong in the structured grid: age, multi-day,
  // ticket-limit text, seatmap link.
  if (hasValue(event.age_restriction)) {
    chips.push({ kind: "age", key: "age",
                 label: event.age_restriction,
                 testid: "enjoy-atm-age" });
  }
  if (event.spans_multiple_days) {
    chips.push({ kind: "multi_day", key: "multi_day",
                 label: "Multi-day", testid: "enjoy-atm-multi-day" });
  }
  if (hasValue(event.ticket_limit)) {
    chips.push({ kind: "ticket_limit", key: "ticket_limit",
                 label: event.ticket_limit, testid: "enjoy-atm-ticket-limit" });
  }
  if (event.seatmap_url) {
    chips.push({
      kind: "seatmap", key: "seatmap", label: "View seatmap ↗",
      href: event.seatmap_url, testid: "enjoy-atm-seatmap",
    });
  }

  if (chips.length === 0) return null;

  return (
    <section data-testid="enjoy-drawer-atmosphere" style={{ marginTop: 18 }}>
      <p className="t-mono uppercase"
         style={{ fontSize: 10, letterSpacing: "0.22em",
                  color: "var(--enjoy-brass-deep)", fontWeight: 600, marginBottom: 10 }}>
        Good to know
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {chips.map((c) => {
          const styleBase = {
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 11.5, letterSpacing: "0.04em",
            padding: "5px 11px", borderRadius: 100,
            background: c.bg || "var(--enjoy-paper)",
            color: c.color || "var(--enjoy-ink)",
            border: c.bg ? "none" : "1px solid var(--enjoy-line)",
            fontWeight: 500,
            textDecoration: "none",
          };
          if (c.href) {
            return (
              <a key={c.key} href={c.href} target="_blank" rel="noopener noreferrer"
                 data-testid={c.testid} style={styleBase}>{c.label}</a>
            );
          }
          return (
            <span key={c.key} data-testid={c.testid} style={styleBase}>{c.label}</span>
          );
        })}
      </div>
    </section>
  );
}

/* ── Smart search input with TM /suggest autocomplete ──────────────────── */
function SuggestSearchInput({ value, onChange, onPick, large = false, placeholder, onSubmit }) {
  const [suggestions, setSuggestions] = useState(null);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  // Debounced suggest call · TM /discovery/v2/suggest (≤2 chars → skip)
  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setSuggestions(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      enjoy.suggest(value)
        .then((r) => { if (!cancelled) setSuggestions(r); })
        .catch(() => { if (!cancelled) setSuggestions(null); });
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [value]);

  const flat = useMemo(() => {
    if (!suggestions) return [];
    const out = [];
    // Curated · founder ruling 14 Feb 2026 PM-6: desk-curated overlays
    // lead the dropdown. The desk's voice first, the provider second.
    for (const c of (suggestions.curated || []).slice(0, 5)) {
      out.push({
        kind: "curated",
        id: c.id,
        name: c.title,
        sub: [c.overlay_kind === "experience" ? "Signature Experience" : null,
              c.venue, c.city, c.category]
              .filter(Boolean).join(" · "),
        date: c.start_at ? c.start_at.slice(0, 10) : null,
      });
    }
    for (const e of (suggestions.events || []).slice(0, 5)) {
      out.push({ kind: "event", id: e.id, name: e.name,
                 sub: [e.venue, e.city].filter(Boolean).join(" · "),
                 date: e.start });
    }
    for (const a of (suggestions.attractions || []).slice(0, 4)) {
      out.push({ kind: "attraction", id: a.id, name: a.name, sub: "Artist" });
    }
    for (const v of (suggestions.venues || []).slice(0, 3)) {
      out.push({ kind: "venue", id: v.id, name: v.name,
                 sub: [v.city, v.country].filter(Boolean).join(" · ") });
    }
    return out;
  }, [suggestions]);

  const isOpen = focused && flat.length > 0 && open;

  // /table-parity hero mode · founder ruling 15 Feb 2026 PM-5.
  // The bar is the primary action; the "Find it" button mirrors
  // /table's "Find the table" right-aligned CTA on a pill-shaped input.
  if (large) {
    return (
      <div style={{ position: "relative", width: "100%" }} data-testid="enjoy-hero-search">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (onSubmit) onSubmit(value);
            setOpen(false);
          }}
          style={{
            display: "flex", alignItems: "center", gap: 0,
            background: "var(--t-card, #fbf6ec)",
            border: `1.5px solid ${focused ? "var(--t-brass, #8b6914)" : "var(--t-line2, #d9cdb7)"}`,
            borderRadius: 18,
            padding: "10px 14px",
            transition: "border-color 200ms ease",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              color: focused ? "var(--t-brass, #8b6914)" : "var(--t-ink3, #8a7d6a)",
              fontSize: 18, paddingLeft: 4,
            }}
          >⌕</span>
          <input
            type="search"
            placeholder={placeholder || "Search any artist, event, or city…"}
            value={value}
            onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIdx(-1); }}
            onFocus={() => { setFocused(true); setOpen(true); }}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={(e) => {
              if (!isOpen) return;
              if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(flat.length - 1, i + 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
              else if (e.key === "Enter" && activeIdx >= 0) {
                e.preventDefault();
                onPick(flat[activeIdx]); setOpen(false);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            data-testid="enjoy-city-search"
            autoComplete="off"
            style={{
              flex: 1, minWidth: 0,
              padding: "8px 12px", border: 0, outline: 0,
              background: "transparent",
              fontSize: 17,
              fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
              color: "var(--t-ink, #1a1614)",
            }}
          />
          {value ? (
            <button
              type="button"
              data-testid="enjoy-search-clear"
              onClick={() => onChange("")}
              aria-label="Clear search"
              style={{
                color: "var(--t-ink3, #8a7d6a)", padding: "0 8px",
                fontSize: 18, background: "transparent", border: 0,
                cursor: "pointer",
              }}
            >×</button>
          ) : null}
          <button
            type="submit"
            data-testid="enjoy-search-submit"
            // Keep focus in the input on press — otherwise the mobile
            // keyboard collapses on blur and the button shifts away from
            // under the member's finger (MC iframe report · 19 Jul 2026).
            onMouseDown={(e) => e.preventDefault()}
            style={{
              background: "var(--t-ink, #1a1614)",
              color: "var(--t-paper2, #fbf6ec)",
              borderRadius: 14, border: 0,
              padding: "13px 24px", minHeight: 44,
              fontSize: 14, fontWeight: 600,
              letterSpacing: "0.01em",
              cursor: "pointer", whiteSpace: "nowrap",
              touchAction: "manipulation",
            }}
          >Find it</button>
        </form>
        {isOpen && (
          <div
            data-testid="enjoy-suggest-popover"
            style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
              background: "var(--t-card)",
              border: "1px solid var(--t-line)",
              borderRadius: 12,
              padding: 6,
              boxShadow: "0 12px 32px rgba(33,28,21,0.12)",
              zIndex: 50,
              maxHeight: 360, overflowY: "auto",
            }}
          >
            {flat.map((s, i) => {
              const showHeading = s.kind === "curated" && (i === 0 || flat[i - 1].kind !== "curated");
              const showOtherHeading = s.kind !== "curated" && (i === 0 || flat[i - 1].kind === "curated");
              const isCurated = s.kind === "curated";
              return (
                <React.Fragment key={`${s.kind}-${s.id}-${i}`}>
                  {showHeading && (
                    <div
                      className="t-mono uppercase"
                      style={{
                        fontSize: 9, letterSpacing: "0.22em",
                        color: "var(--t-brass, #8b6914)",
                        padding: "6px 10px 4px", fontWeight: 600,
                      }}
                    >Curated by Concierge®</div>
                  )}
                  {showOtherHeading && (
                    <div
                      className="t-mono uppercase"
                      style={{
                        fontSize: 9, letterSpacing: "0.22em",
                        color: "var(--t-ink3, #8a7d6a)",
                        padding: "6px 10px 4px", fontWeight: 600,
                      }}
                    >From Ticketmaster</div>
                  )}
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onPick(s); setOpen(false); }}
                    onMouseEnter={() => setActiveIdx(i)}
                    data-testid={isCurated ? `enjoy-suggest-curated-${s.id}` : `enjoy-suggest-${s.kind}-${i}`}
                    style={{
                      width: "100%", textAlign: "left",
                      padding: "9px 12px", borderRadius: 8,
                      background: i === activeIdx
                        ? (isCurated ? "var(--t-brass-wash, rgba(160,122,56,0.10))" : "var(--t-paper, #f4ecd8)")
                        : "transparent",
                      border: 0, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13.5,
                        color: isCurated ? "var(--t-brass-deep, #7d5b21)" : "var(--t-ink, #1a1614)",
                        fontWeight: isCurated ? 600 : 500,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{s.name}</div>
                      {s.sub && (
                        <div style={{
                          fontSize: 11, color: "var(--t-ink3, #8a7d6a)",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          marginTop: 1,
                        }}>{s.sub}</div>
                      )}
                    </div>
                    {s.date && (
                      <span
                        className="t-mono"
                        style={{
                          fontSize: 10, color: "var(--t-ink3, #8a7d6a)",
                          letterSpacing: "0.06em", whiteSpace: "nowrap",
                        }}
                      >{s.date}</span>
                    )}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Legacy compact pill mode (kept for any callers that still want the
  // small bar inside the filter row).

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
      <input
        placeholder="Search any artist, event or city…"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIdx(-1); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onKeyDown={(e) => {
          if (!isOpen) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(flat.length - 1, i + 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
          else if (e.key === "Enter" && activeIdx >= 0) {
            e.preventDefault();
            onPick(flat[activeIdx]); setOpen(false);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        data-testid="enjoy-city-search"
        style={{
          width: "100%",
          padding: "10px 16px", borderRadius: 100,
          background: "var(--t-card)", border: "1px solid var(--t-line)",
          fontSize: 13.5, color: "var(--t-ink)", outline: "none",
        }}
      />
      {isOpen && (
        <div
          data-testid="enjoy-suggest-popover"
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
            background: "var(--t-card)",
            border: "1px solid var(--t-line)",
            borderRadius: 12,
            padding: 6,
            boxShadow: "0 12px 32px rgba(33,28,21,0.12)",
            zIndex: 50,
            maxHeight: 360, overflowY: "auto",
          }}
        >
          {flat.map((s, i) => {
            // Curated section heading · only above the first curated row.
            const showHeading = s.kind === "curated" && (i === 0 || flat[i - 1].kind !== "curated");
            const showOtherHeading = s.kind !== "curated" && (i === 0 || flat[i - 1].kind === "curated");
            const isCurated = s.kind === "curated";
            return (
              <React.Fragment key={`${s.kind}-${s.id}-${i}`}>
                {showHeading && (
                  <div
                    data-testid="enjoy-suggest-heading-curated"
                    className="t-mono uppercase"
                    style={{
                      fontSize: 9.5, letterSpacing: "0.22em",
                      color: "var(--t-brass)",
                      padding: "10px 12px 4px",
                      fontWeight: 600,
                      display: "flex", alignItems: "center", gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 12, color: "var(--t-brass)" }}>✦</span>
                    Concierge® · curated
                  </div>
                )}
                {showOtherHeading && flat.some((x) => x.kind === "curated") && (
                  <div
                    data-testid="enjoy-suggest-heading-tm"
                    className="t-mono uppercase"
                    style={{
                      fontSize: 9.5, letterSpacing: "0.22em",
                      color: "var(--t-ink3)",
                      padding: "10px 12px 4px",
                      fontWeight: 600,
                      borderTop: "1px solid var(--t-line)",
                      marginTop: 4,
                    }}
                  >
                    From Ticketmaster
                  </div>
                )}
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onPick(s); setOpen(false); }}
                  onMouseEnter={() => setActiveIdx(i)}
                  data-testid={`enjoy-suggest-${s.kind}-${i}`}
                  style={{
                    width: "100%", textAlign: "left",
                    padding: "9px 12px", borderRadius: 8,
                    background: i === activeIdx
                      ? (isCurated ? "var(--t-brass-wash)" : "var(--t-paper2)")
                      : "transparent",
                    border: 0, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                >
                  <span className="t-mono uppercase"
                    style={{
                      fontSize: 9, letterSpacing: "0.16em",
                      color: isCurated ? "var(--t-brass-deep)" : "var(--t-brass)",
                      width: 60, fontWeight: 600,
                    }}>
                    {isCurated ? "✦ Desk" : s.kind}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 14,
                      color: "var(--t-ink)",
                      fontWeight: isCurated ? 600 : 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {s.name}
                    </div>
                    {s.sub && (
                      <div style={{
                        fontSize: 11.5, color: "var(--t-ink3)",
                        marginTop: 2,
                        whiteSpace: "nowrap", overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {s.sub}{s.date ? ` · ${s.date}` : ""}
                      </div>
                    )}
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventCard({ event, index = 0, onSelect }) {
  const initial = (event.title || "?").trim()[0]?.toUpperCase() || "?";
  const isExperience = event.kind === "experience";
  const dateLine = fmtDateRange(event.start_at, event.end_at, event.date_note, event.kind);
  const place = [event.venue_name, event.city].filter(Boolean).join(" · ")
              || event.city
              || event.country
              || "Location TBD";
  const whisper = event.concierge_whisper;

  // FIR-20260217-TMSG · IntersectionObserver fires one impression beacon
  // per session per event_id when the card scrolls into view (50% visible).
  const _cardRef = useRef(null);
  useEffect(() => {
    if (!_cardRef.current || typeof IntersectionObserver === "undefined") return;
    const el = _cardRef.current;
    const io = new IntersectionObserver((entries) => {
      for (const ent of entries) {
        if (ent.isIntersecting && ent.intersectionRatio >= 0.5) {
          enjoyFireImpression(event);
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [event.event_id, event.source]);  // eslint-disable-line react-hooks/exhaustive-deps

  // K1.1 (founder ruling 14 Jun 2026 late · "Enjoy/Table card parity
  // with Escape"): the member-facing chip strips the supplier name.
  // We still resolve the underlying route so it can ride along as
  // `data-provider-kind` for admin/provenance · the visible LABEL
  // is curated: Concierge® for desk-curated experiences, "Book" for
  // ticketed events, "View" as fallback. The actual ticket / concierge
  // URL continues to fire from the drawer · this chip is visual hygiene.
  //
  // FIR-20260217-TMSG · founder ruling 17 Feb 2026 PM (chip honesty):
  // When the status gate has classified this event as non-0a (offsale,
  // pre-sale, cancelled, postponed, rescheduled), the chip MUST read
  // "Concierge®" instead of "Book". A card showing "OFF SALE" at the
  // top and "⚡ Book" at the bottom contradicts itself — the chip is the
  // same family of lie as the badge bug we just fixed. Whole card,
  // one story.
  const memberChip = (() => {
    if (isExperience) {
      return { label: "by Concierge\u00ae", icon: "\u2726", tone: "brass", providerKind: "concierge" };
    }
    const _bucket = (event.booking && event.booking.status_gate && event.booking.status_gate.bucket) || null;
    const _isGated = _bucket && _bucket !== "0a";
    const tier1 = (event.booking?.ladder || []).find(
      (r) => r && r.url && r.kind
    );
    const providerKind = tier1?.kind
      || (event.source === "ticketmaster" ? "ticketmaster" : null)
      || (event.is_curated || event.source === "curated" ? "curated" : null);
    if (event.is_curated || event.source === "curated") {
      return { label: "by Concierge\u00ae", icon: "\u2726", tone: "brass", providerKind: "curated" };
    }
    if (_isGated) {
      // Card whispers the same truth top-to-bottom: badge says OFF SALE /
      // CANCELLED / POSTPONED, chip says Concierge®, drawer routes to the
      // desk with the reason code. No more "Book" on a gated card.
      return { label: "Concierge\u00ae", icon: "\u2726", tone: "brass", providerKind: "concierge_gated" };
    }
    if (providerKind) {
      return { label: "Book", icon: "\u26a1", tone: "brass", providerKind };
    }
    return null;
  })();

  return (
    <div
      ref={_cardRef}
      data-testid={`enjoy-card-${event.source}-${event.event_id}`}
      data-sale-status={event.sale_status || "unknown"}
      role="button"
      tabIndex={0}
      onClick={() => { enjoyFireClick(event, "drawer_open"); onSelect(event); }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          enjoyFireClick(event, "drawer_open");
          onSelect(event);
        }
      }}
      className="t-rise cursor-pointer group transition-all duration-300"
      style={{
        animationDelay: `${Math.min(index, 12) * 0.05}s`,
        background: "var(--t-card)",
        border: "1px solid var(--t-line)",
        borderRadius: 18,
        overflow: "hidden",
        // Cancelled / sold-out events get a subtle muted treatment so members
        // see them as a signal, not a misclick. Drawer carries the full state.
        opacity: (event.sale_status === "cancelled") ? 0.62 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--t-brass2)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--t-line)";
        e.currentTarget.style.transform = "none";
      }}
    >
      {/* Hero band · founder ruling 16 Feb 2026 PM-12 · Workstream A.
          For Signature Experiences the wire ships `hero_image_url`
          (proxy route, no API key) + `hero_image_source_type`. For TM
          / curated events the legacy `image_url` still wins. The
          attribution pill renders ONLY when source=google_places. */}
      {(() => {
        const heroSrc = event.hero_image_url || event.image_url;
        const isPlaces = event.hero_image_source_type === "google_places";
        const credit = isPlaces && Array.isArray(event.google_photo_html_attributions)
          ? event.google_photo_html_attributions[0]
          : null;
        return (
          <div
            className="relative overflow-hidden flex items-center justify-center"
            style={{
              height: 168,
              background: TINTS[index % TINTS.length],
            }}
          >
            {heroSrc ? (
              <>
                <CldImg
                  src={heroSrc}
                  w={720}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
                  style={{ transform: "scale(1)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  data-testid="enjoy-card-photo"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: "linear-gradient(to top, rgba(33,28,21,0.45) 0%, rgba(33,28,21,0) 55%)",
                  }}
                />
                {credit && (
                  <span
                    data-testid="enjoy-card-photo-attribution"
                    className="absolute t-mono"
                    style={{
                      /* Position high enough to clear the price chip
                         at the very bottom (which lives at bottom:10,
                         left:14). 32px clears the price line cleanly. */
                      bottom: 32, left: 12,
                      fontSize: 9, letterSpacing: "0.04em",
                      color: "#fff",
                      background: "rgba(0,0,0,0.55)",
                      backdropFilter: "blur(4px)",
                      borderRadius: 100,
                      padding: "2px 8px",
                      maxWidth: "calc(100% - 110px)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                    dangerouslySetInnerHTML={{ __html: `Photo · ${credit}` }}
                  />
                )}
              </>
            ) : (
              /* FIR-20260217-TMSG · Icon-card fallback · founder ruling
                 17 Feb 2026 PM (option swap of stock photos for icon
                 cards). When the swirl detector cleared image_url, OR
                 the card has no image_url for any reason, render an
                 elegant designed card: brand teal/ink gradient + a
                 large refined Lucide line-icon by category + subtle
                 hairline grid + category eyebrow text. Never empty,
                 never broken-looking, never lies that it's the venue. */
              (() => {
                const cat = String(event.category || "").toLowerCase();
                const Icon =
                  cat.includes("theatre") || cat.includes("drama") || cat.includes("musical") ? Drama
                  : cat.includes("music") || cat.includes("concert") || cat.includes("opera") ? Music
                  : cat.includes("comedy") ? MicVocal
                  : cat.includes("club") || cat.includes("night") || cat.includes("dance") ? Disc3
                  : cat.includes("family") || cat.includes("kid") ? Smile
                  : cat.includes("sport") ? Trophy
                  : cat.includes("art") || cat.includes("gallery") || cat.includes("museum") || cat.includes("exhibition") ? Palette
                  : cat.includes("festival") ? Sparkles
                  : cat.includes("film") || cat.includes("cinema") ? Film
                  : Star;
                return (
                  <div
                    data-testid="enjoy-card-icon-fallback"
                    data-fallback-category={cat || "uncategorised"}
                    className="absolute inset-0 flex flex-col items-center justify-center"
                    style={{
                      background:
                        "radial-gradient(70% 110% at 28% 22%, rgba(255,255,255,0.06), rgba(255,255,255,0) 55%), " +
                        "radial-gradient(60% 110% at 100% 100%, rgba(160,122,78,0.18), rgba(160,122,78,0) 60%), " +
                        "linear-gradient(150deg, #1F4A40 0%, #16332C 60%, #0F2520 100%)",
                    }}
                  >
                    {/* subtle hairline grid · designed feel, not broken */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage:
                          "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), " +
                          "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
                        backgroundSize: "32px 32px",
                        maskImage: "radial-gradient(120% 80% at 50% 60%, #000 30%, transparent 80%)",
                        WebkitMaskImage: "radial-gradient(120% 80% at 50% 60%, #000 30%, transparent 80%)",
                      }}
                    />
                    <Icon
                      size={52}
                      strokeWidth={1.1}
                      color="#E8D2A8"
                      style={{ position: "relative", zIndex: 2, opacity: 0.92 }}
                    />
                    <span
                      style={{
                        position: "relative",
                        zIndex: 2,
                        marginTop: 12,
                        fontSize: 9,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: "#D8C99A",
                        fontFamily: "Inter, system-ui, sans-serif",
                        fontWeight: 600,
                      }}
                    >{(event.category || "Live event")}</span>
                  </div>
                );
              })()
            )}

            {/* Top-right · K1.1 member-facing parity chip · strips
                supplier names ("via Ticketmaster" / "via DICE" etc.).
                The actual booking link still fires from the drawer ·
                this chip is visual hygiene only. Provider kind rides
                along as data-provider-kind for admin/provenance. */}
            {memberChip && (
              <span
                className="absolute t-mono"
                data-testid="enjoy-card-provider-pill"
                data-provider-kind={memberChip.providerKind}
                data-member-chip-label={memberChip.label}
                style={{
                  top: 12, right: 12,
                  fontSize: 9.5, letterSpacing: "0.10em",
                  color: "var(--t-brass-deep, var(--t-brass))",
                  background: "rgba(252,250,244,0.94)",
                  border: "1px solid var(--t-brass2)",
                  borderRadius: 20,
                  padding: "4px 11px",
                  fontWeight: 600,
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 2px 8px rgba(33,28,21,0.12)",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <span style={{ color: "var(--t-brass)", fontSize: 11 }}>{memberChip.icon}</span>
                {memberChip.label}
              </span>
            )}

            {/* Bottom-right · category chip (smaller, atmospheric) */}
            <span
              className="absolute t-mono uppercase"
              style={{
                bottom: 10, right: 12,
                fontSize: 9, letterSpacing: "0.14em",
                color: heroSrc ? "rgba(252,250,244,0.96)" : "var(--t-brass)",
                background: heroSrc ? "rgba(33,28,21,0.55)" : "var(--t-brass-wash)",
                border: heroSrc ? "1px solid rgba(252,250,244,0.32)" : "1px solid var(--t-brass2)",
                borderRadius: 20,
                padding: "3px 9px",
                fontWeight: 600,
              }}
          data-testid="enjoy-card-category-chip"
        >
          {isExperience ? "Experience" : (event.category || "event")}
        </span>

        {/* Top-left · Sale status badge · label via resolveSaleBadge for
            sentinel-aware honesty (FIR-20260217-TMSG) */}
        {(() => {
          const _b = resolveSaleBadge(event);
          if (!_b) return null;
          return (
            <span
              className="absolute t-mono uppercase"
              data-testid="enjoy-card-sale-status"
              data-sale-status={event.sale_status}
              data-sale-badge-label={_b.label}
              style={{
                top: 12, left: 12,
                fontSize: 9, letterSpacing: "0.16em",
                color: _b.color,
                background: _b.bg,
                borderRadius: 20,
                padding: "3px 11px",
                fontWeight: 600,
                boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
              }}
            >{_b.label}</span>
          );
        })()}

        {/* Top-left · Curated chip (only on overlays · suppressed when sale_status present) */}
        {event.is_curated && !event.sale_status && (
          <span
            className="absolute t-mono uppercase"
            data-testid="enjoy-card-curated-flag"
            style={{
              top: 12, left: 12,
              fontSize: 9, letterSpacing: "0.14em",
              color: "var(--t-brass)",
              background: "var(--t-brass-wash)",
              border: "1px solid var(--t-brass2)",
              borderRadius: 20,
              padding: "3px 9px",
              fontWeight: 600,
            }}
          >Curated</span>
        )}

        {/* Bottom-left · Price */}
        <span
          className="absolute t-mono"
          data-testid="enjoy-card-price"
          style={{
            bottom: 10, left: 14,
            fontSize: 10, letterSpacing: "0.1em",
            color: heroSrc ? "rgba(252,250,244,0.92)" : "var(--t-ink2)",
            textShadow: heroSrc ? "0 1px 2px rgba(0,0,0,0.4)" : "none",
          }}
        >{priceLine(event)}</span>
      </div>
        );
      })()}

      <div style={{ padding: "15px 18px 18px" }}>
        <h3
          data-testid="enjoy-card-title"
          className="t-serif"
          style={{
            fontSize: 21, fontWeight: 500, lineHeight: 1.15,
            letterSpacing: "-0.01em", color: "var(--t-ink)",
            margin: 0, minHeight: 48,
          }}
        >{event.title}</h3>

        <p
          data-testid="enjoy-card-place"
          style={{ fontSize: 12.5, color: "var(--t-ink2)", marginTop: 6, lineHeight: 1.4 }}
        >{place}</p>

        <p
          data-testid="enjoy-card-date"
          className="t-mono uppercase"
          style={{
            fontSize: 10.5, color: "var(--t-brass)",
            letterSpacing: "0.1em", marginTop: 4, fontWeight: 500,
          }}
        >{dateLine}</p>

        {/* Genre / sub-genre line · TM harvest · sits below the date as
            a small atmospheric eyebrow, matching /table's "Held in regard"
            rhythm. Only renders when TM gave us a genre we trust. */}
        {(event.genre || event.sub_genre) && (
          <p
            data-testid="enjoy-card-genre"
            className="t-mono"
            style={{
              fontSize: 11, color: "var(--t-ink3)",
              letterSpacing: "0.06em", marginTop: 4, fontStyle: "italic",
            }}
          >
            {[event.genre, event.sub_genre].filter(Boolean).join(" · ")}
            {event.attractions && event.attractions.length > 1
              ? ` · ${event.attractions.length} acts`
              : ""}
          </p>
        )}

        {whisper && (
          <p
            data-testid="enjoy-card-whisper"
            className="t-serif"
            title={whisper}
            style={{
              fontSize: 13.5, color: "var(--t-ink)", lineHeight: 1.5,
              marginTop: 11, minHeight: 44, fontStyle: "italic",
            }}
          >{whisper.length > 180 ? whisper.slice(0, 178).trim() + "…" : whisper}</p>
        )}

        {/* Editorial why-go preview · mira_editorial provenance · only
            renders when Mira has produced something honest. Sits below
            the whisper so the desk's voice leads · Mira's is supporting. */}
        {event.editorial?.why_go && (
          <p
            data-testid="enjoy-card-editorial-why-go"
            style={{
              fontSize: 12.5, color: "var(--t-ink2)",
              lineHeight: 1.5, marginTop: 8,
              paddingTop: 8,
              borderTop: "1px dashed var(--t-line2)",
            }}
            title="Concierge® whisper · editorial layer (prose from venue facts + desk whisper; never invents lineup/price/date)"
          >
            <span
              className="t-mono uppercase"
              style={{
                fontSize: 8.5, letterSpacing: "0.22em",
                color: "var(--t-brass)", fontWeight: 600,
                marginRight: 8,
              }}
            >Concierge® whisper</span>
            {event.editorial.why_go.length > 130
              ? event.editorial.why_go.slice(0, 128).trim() + "…"
              : event.editorial.why_go}
          </p>
        )}

        {/* Demand pulse chip on the card · honest count, not a vibe */}
        {event.editorial?.demand_pulse?.count >= 3 && (
          <p
            data-testid="enjoy-card-demand-pulse"
            className="t-mono uppercase"
            style={{
              fontSize: 9, letterSpacing: "0.18em",
              color: "var(--t-brass-deep)",
              marginTop: 8, fontWeight: 600,
            }}
          >★ Requested {event.editorial.demand_pulse.count}× this week</p>
        )}

        {event.collection && (
          <p
            data-testid="enjoy-card-collection"
            className="t-mono uppercase"
            style={{
              fontSize: 9, color: "var(--t-ink3)",
              letterSpacing: "0.16em", marginTop: 10,
            }}
          >{event.collection}</p>
        )}

        <div className="t-hairline my-3" />

        {/* Route chip · Addendum B v2 §2 · top of ladder OR Concierge®-only */}
        <RouteChipRow event={event} />
      </div>
    </div>
  );
}

/* ── Route chip on the card · same shape /table uses ──────────────────── */
function RouteChipRow({ event }) {
  const booking = event.booking || null;
  const tier = booking?.tier ?? 3;
  const chip = booking?.card_chip;
  const isExperience = event.kind === "experience";
  const isByArrangement = isExperience || event.force_concierge;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
        {isByArrangement ? (
          <span
            data-testid="enjoy-card-route-byarrangement"
            className="t-mono uppercase"
            style={{
              fontSize: 9, letterSpacing: "0.18em",
              color: "var(--t-brass)", fontWeight: 600,
              background: "var(--t-brass-wash)",
              border: "1px solid var(--t-brass2)",
              padding: "3px 9px", borderRadius: 100,
            }}
          >By arrangement</span>
        ) : tier === 1 && chip ? (
          <div className="flex flex-col" style={{ minWidth: 0, gap: 4 }}>
            <span
              data-testid="enjoy-card-route-book"
              className="t-mono"
              style={{
                fontSize: 10, letterSpacing: "0.06em",
                color: "var(--t-verified)", fontWeight: 600,
                background: "var(--t-verified-wash)",
                border: "1px solid var(--t-verified)",
                padding: "3px 10px", borderRadius: 100,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                maxWidth: 240, display: "inline-block",
              }}
              title={chip.url}
            >{chip.label} ↗</span>
            {/* K1.2 (14 Jun 2026 evening · founder ruling): the
                "official · Ticketmaster" / supplier sub-label is
                REMOVED from the front of the card · members see a
                clean primary chip. Supplier identity is preserved
                in chip.kind for telemetry and admin/provenance. */}
            {chip.verified_at && (
              <span
                data-testid="enjoy-card-route-trail"
                className="t-mono"
                style={{
                  fontSize: 9, letterSpacing: "0.08em",
                  color: "var(--t-ink3)",
                  paddingLeft: 4,
                }}
              >{verifiedTrail(chip.verified_at)}</span>
            )}
          </div>
        ) : tier === 2 && chip ? (
          <span
            data-testid="enjoy-card-route-find"
            className="t-mono"
            style={{
              fontSize: 10, letterSpacing: "0.06em",
              color: "var(--t-pending)", fontWeight: 600,
              background: "var(--t-pending-wash)",
              border: "1px solid var(--t-pending)",
              padding: "3px 10px", borderRadius: 100,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              maxWidth: 220,
            }}
            title={chip.url}
          >{chip.label} ↗</span>
        ) : (
          <span
            data-testid="enjoy-card-route-concierge"
            className="t-mono uppercase"
            style={{
              fontSize: 9, letterSpacing: "0.18em",
              color: "var(--t-brass)", fontWeight: 600,
              background: "var(--t-brass-wash)",
              border: "1px solid var(--t-brass2)",
              padding: "3px 9px", borderRadius: 100,
            }}
          >Concierge® only</span>
        )}
      </div>
      <span
        className="transition-transform group-hover:translate-x-1"
        style={{ fontSize: 13, fontWeight: 600, color: "var(--t-brass)" }}
        aria-hidden="true"
      >→</span>
    </div>
  );
}


/* ── Access Route Block · founder ruling 16 Feb 2026 PM-9 ──────────────
 * One clean member-facing access surface that replaces the multi-supplier
 * marketplace. Member sees ONE block · "Verified access route" + 4 trust
 * pills + "Request via Concierge®" CTA. The supplier list (the old
 * "via Keith Prowse / Sportsworld / F1 Experiences" cards) is moved into
 * a role-gated, collapsed "Desk routing details" block at the bottom —
 * visible only to admin/desk users.
 *
 * Doctrine pin: members never choose a broker. The desk holds the door.
 * Supplier names appear member-facing ONLY when they're official AND
 * publicly meaningful (Ticketmaster · FIFA · F1 · The Open). Cornucopia
 * and other white-label routes stay desk-only.
 * ────────────────────────────────────────────────────────────────────── */

// Official-source hosts that may be named to members in "Official tickets
// via {Supplier}" copy. Doctrine: match the HOSTNAME only — never the URL
// path · otherwise a broker URL like sportsworld.co.uk/wimbledon-tickets/
// would falsely register as "official Wimbledon" hospitality.
const OFFICIAL_NAMED_HOSTS = [
  "ticketmaster.com", "ticketmaster.co.uk", "ticketmaster.de",
  "ticketmaster.fr", "ticketmaster.it", "ticketmaster.es",
  "ticketmaster.ie", "ticketmaster.com.au", "ticketmaster.ca",
  "fifa.com", "hospitality.fifa.com", "fifaworldcup26.hospitality.fifa.com",
  "formula1.com", "f1experiences.com",
  "theopen.com", "rolandgarros.com", "billetterie.rolandgarros.com",
  "wimbledon.com",
  "ascot.com", "ascot.co.uk",
  "henley-royal-regatta.com", "hrr.co.uk",
  "goodwood.com",
  "olympics.com",
  "rolex.com",
];

function _railHost(rail) {
  try {
    const u = new URL((rail && rail.url) || "");
    let h = (u.hostname || "").toLowerCase();
    if (h.startsWith("www.")) h = h.slice(4);
    return h;
  } catch (_e) {
    return "";
  }
}

function _railIsOfficialNamed(rail) {
  const h = _railHost(rail);
  if (!h) return false;
  // Exact hostname match OR suffix match (e.g. "hospitality.fifa.com"
  // matches "fifa.com" because subdomain.fifa.com endsWith ".fifa.com").
  return OFFICIAL_NAMED_HOSTS.some(
    (host) => h === host || h.endsWith("." + host)
  );
}

function AccessRouteBlock({ event, hospitalityRails, onRequestConcierge, isAdmin }) {
  const [deskOpen, setDeskOpen] = React.useState(false);
  const [notesOpen, setNotesOpen] = React.useState(false);

  const namedOfficial = (hospitalityRails || []).find(_railIsOfficialNamed);
  const memberFacingSupplierLine = namedOfficial
    ? `Official hospitality via ${namedOfficial.label}`
    : null;

  const conciergeNote = (() => {
    const t = (event && event.title) || "this night";
    return (
      `${t} sits with the Concierge®'s verified access network. ` +
      `Official hospitality, debenture and trusted-partner routes may all be live — ` +
      `nothing is held or quoted until live availability is verified.`
    );
  })();

  return (
    <section
      data-testid="enjoy-drawer-access-route"
      data-event-slug={event && event.hospitality_event_slug ? event.hospitality_event_slug : ""}
      style={{
        width: "100%",
        marginTop: 8,
        padding: "16px 0 4px",
        borderTop: "1px dashed var(--enjoy-line)",
      }}
    >
      <p
        className="t-mono uppercase"
        style={{
          fontSize: 9.5, letterSpacing: "0.22em",
          color: "var(--enjoy-brass-deep)",
          margin: "0 0 8px", fontWeight: 600,
          display: "flex", alignItems: "center", gap: 6,
        }}
        data-testid="enjoy-drawer-access-heading"
      >
        <span style={{ color: "var(--enjoy-brass)" }}>✦</span>
        Verified access route
      </p>

      {memberFacingSupplierLine && (
        <p
          data-testid="enjoy-drawer-official-named"
          style={{
            fontSize: 13, color: "var(--enjoy-ink)",
            margin: "0 0 8px", fontWeight: 500,
          }}
        >
          {memberFacingSupplierLine}
        </p>
      )}

      <p
        style={{
          fontSize: 12.5, color: "var(--enjoy-muted)",
          lineHeight: 1.55, margin: "0 0 12px",
        }}
        data-testid="enjoy-drawer-access-note"
      >
        {conciergeNote}
      </p>

      <div
        data-testid="enjoy-drawer-access-pills"
        style={{
          display: "flex", flexWrap: "wrap", gap: 6,
          margin: "0 0 14px",
        }}
      >
        {["Verified routes available", "Live availability check required",
          "Price on request", "Concierge® will route"].map((t) => (
          <span
            key={t}
            data-testid={`enjoy-drawer-pill-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            style={{
              fontSize: 10.5, letterSpacing: "0.05em",
              color: "var(--enjoy-brass-deep)",
              background: "var(--enjoy-brass-wash, rgba(176, 138, 81, 0.08))",
              border: "1px solid var(--enjoy-brass-soft, rgba(176, 138, 81, 0.28))",
              borderRadius: 100, padding: "4px 10px",
              fontWeight: 500,
            }}
          >{t}</span>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onRequestConcierge && onRequestConcierge(event)}
        data-testid="enjoy-drawer-access-request"
        className="enjoy-btn"
        style={{
          width: "100%", justifyContent: "center",
          padding: "12px 0", fontSize: 13.5, fontWeight: 600,
        }}
      >
        Request via Concierge®
      </button>

      <button
        type="button"
        onClick={() => setNotesOpen((v) => !v)}
        data-testid="enjoy-drawer-access-notes-toggle"
        style={{
          background: "none", border: "none", cursor: "pointer",
          color: "var(--enjoy-muted)", fontSize: 12,
          padding: "10px 0 0", display: "inline-flex", alignItems: "center", gap: 6,
        }}
      >
        {notesOpen ? "Hide access notes" : "View access notes"}
        <span style={{ fontSize: 11 }}>{notesOpen ? "−" : "+"}</span>
      </button>
      {notesOpen && (
        <div
          data-testid="enjoy-drawer-access-notes"
          style={{
            marginTop: 6, padding: "10px 0",
            fontSize: 12, color: "var(--enjoy-muted)",
            lineHeight: 1.5, borderTop: "1px dashed var(--enjoy-line)",
          }}
        >
          The Concierge® will check official hospitality, debenture and trusted
          partner routes before confirming. Seating quality, dress code,
          food &amp; beverage inclusions and cancellation terms vary by route —
          your concierge will brief you with the comparison before booking.
        </div>
      )}

      {isAdmin && (
        <section
          data-testid="enjoy-drawer-desk-routing"
          style={{
            marginTop: 16, padding: "10px 12px",
            border: "1px dashed var(--enjoy-brass-soft, rgba(176, 138, 81, 0.32))",
            borderRadius: 6,
            background: "var(--enjoy-brass-wash, rgba(176, 138, 81, 0.05))",
          }}
        >
          <button
            type="button"
            onClick={() => setDeskOpen((v) => !v)}
            data-testid="enjoy-drawer-desk-routing-toggle"
            style={{
              background: "none", border: "none", cursor: "pointer",
              width: "100%", display: "flex", alignItems: "center",
              justifyContent: "space-between", padding: 0,
              color: "var(--enjoy-brass-deep)", fontSize: 11,
              letterSpacing: "0.18em", textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            <span>Desk routing details · admin only</span>
            <span>{deskOpen ? "−" : "+"}</span>
          </button>
          {deskOpen && (
            <div
              data-testid="enjoy-drawer-desk-routing-body"
              style={{ marginTop: 10 }}
            >
              {(hospitalityRails || []).map((rail, i) => (
                <div
                  key={`desk-${rail.kind}-${i}`}
                  data-testid={`enjoy-drawer-desk-rail-${rail.kind}`}
                  style={{
                    display: "flex", alignItems: "flex-start",
                    justifyContent: "space-between", gap: 10,
                    padding: "8px 0",
                    borderTop: i === 0 ? "none" : "1px dotted var(--enjoy-line)",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--enjoy-ink)" }}>
                      {rail.label}
                    </div>
                    {rail.rationale && (
                      <div style={{
                        fontSize: 11, color: "var(--enjoy-muted)",
                        lineHeight: 1.4, marginTop: 2,
                      }}>{rail.rationale}</div>
                    )}
                    {rail.kind && (
                      <div className="t-mono uppercase" style={{
                        fontSize: 9, letterSpacing: "0.16em",
                        color: "var(--enjoy-brass-deep)", marginTop: 4,
                      }}>type: {rail.kind}</div>
                    )}
                  </div>
                  <a
                    href={rail.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`enjoy-drawer-desk-rail-${rail.kind}-open`}
                    style={{
                      fontSize: 10, letterSpacing: "0.18em",
                      color: "var(--enjoy-brass-deep)",
                      textDecoration: "none", whiteSpace: "nowrap",
                      fontWeight: 600, padding: "4px 8px",
                      border: "1px solid var(--enjoy-brass-soft, rgba(176, 138, 81, 0.32))",
                      borderRadius: 3,
                    }}
                  >Open ↗</a>
                </div>
              ))}
              <div style={{
                marginTop: 10, padding: "8px 0 0",
                borderTop: "1px dotted var(--enjoy-line)",
                fontSize: 11, color: "var(--enjoy-muted)", lineHeight: 1.45,
              }}>
                Member view does not show this list. The desk picks the route
                based on availability, terms and margin. Members see
                <em> Verified access route · Request via Concierge®</em>.
              </div>
            </div>
          )}
        </section>
      )}
    </section>
  );
}


/* ── Drawer ───────────────────────────────────────────────────────────── */
function EventDrawer({ event: _searchEvent, onClose, onBookDirect, onRequestConcierge, isAdminViewer = false }) {
  // ── Lazy enrichment · founder ruling 14 Feb 2026 PM-4 ──
  // On drawer-open we call /api/enjoy/event/{source}/{id}/enrich which
  // fans out to TM detail + linked attractions (artist photos!) + linked
  // venue (lat/lng). The merged event below replaces the search-stub
  // transparently — the body keeps using `event` so no churn here.
  const [enriched, setEnriched] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const _enrichmentInFlight = React.useRef({});
  useEffect(() => {
    if (!_searchEvent) return;
    if (_searchEvent.source !== "ticketmaster") return;
    const key = `${_searchEvent.source}:${_searchEvent.event_id}`;
    // Dedupe: skip the second StrictMode-fired call for the same key.
    if (_enrichmentInFlight.current[key]) return;
    _enrichmentInFlight.current[key] = true;
    let cancelled = false;
    setEnriching(true);
    setEnriched(null);
    enjoy.enrich(_searchEvent.source, _searchEvent.event_id)
      .then((r) => { if (!cancelled) setEnriched(r?.enriched || null); })
      .catch(() => { /* fall through to search-mode data */ })
      .finally(() => {
        if (!cancelled) setEnriching(false);
        delete _enrichmentInFlight.current[key];
      });
    return () => { cancelled = true; };
  }, [_searchEvent && _searchEvent.event_id, _searchEvent && _searchEvent.source]);

  // Merge enrichment over the search-stub event so the rest of the
  // drawer renders the upgraded fields transparently.
  const event = useMemo(() => {
    if (!_searchEvent) return null;
    if (!enriched) return _searchEvent;
    return {
      ..._searchEvent,
      image_url: enriched.hero_image || _searchEvent.image_url,
      images_gallery: enriched.images_gallery || _searchEvent.images_gallery,
      attractions: enriched.attractions || _searchEvent.attractions,
      genre: enriched.genre || _searchEvent.genre,
      sub_genre: enriched.sub_genre || _searchEvent.sub_genre,
      sale_status: enriched.sale_status || _searchEvent.sale_status,
      sale_starts_at: enriched.sale_starts_at || _searchEvent.sale_starts_at,
      spans_multiple_days: enriched.spans_multiple_days ?? _searchEvent.spans_multiple_days,
      price_from: enriched.price_from ?? _searchEvent.price_from,
      price_to: enriched.price_to ?? _searchEvent.price_to,
      currency: enriched.currency || _searchEvent.currency,
      ticket_limit: enriched.ticket_limit || _searchEvent.ticket_limit,
      seatmap_url: enriched.seatmap_url || _searchEvent.seatmap_url,
      promoter: enriched.promoter || _searchEvent.promoter,
      info: enriched.info || _searchEvent.info,
      please_note: enriched.please_note || _searchEvent.please_note,
      age_restriction: enriched.age_restriction || _searchEvent.age_restriction,
      accessibility_notes: enriched.accessibility_notes || _searchEvent.accessibility_notes,
      parking_notes: enriched.parking_notes || _searchEvent.parking_notes,
      venue_general_rule: enriched.venue_general_rule || _searchEvent.venue_general_rule,
      venue_lat: enriched.venue_lat,
      venue_lon: enriched.venue_lon,
      venue_full_address: enriched.venue_full_address,
      venue_box_office: enriched.venue_box_office,
    };
  }, [_searchEvent, enriched]);

  // G6 · "Dine before the show" cross-pillar strip ·
  // calls /api/enjoy/event/{source}/{id}/dine-nearby once we have an
  // event with venue coords. Soft-fails to empty.
  const [dineNearby, setDineNearby] = useState(null);
  useEffect(() => {
    if (!event?.event_id || !event?.source) return;
    const lat = event.venue_lat ?? event.lat;
    const lng = event.venue_lon ?? event.lng;
    if (lat == null || lng == null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${_ENJOY_BACKEND}/api/enjoy/event/${event.source}/${encodeURIComponent(event.event_id)}/dine-nearby`);
        const data = await r.json();
        if (!cancelled && data?.ok && Array.isArray(data.cards)) {
          setDineNearby(data.cards);
        }
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [event?.event_id, event?.source, event?.venue_lat, event?.venue_lon]);

  // Concierge® voice line · Parity Sprint (30 Jun 2026). Only fires
  // when the row has no desk-curated `concierge_whisper`; the LLM
  // generates an atmospheric one-liner from the event + venue facts.
  const [voiceLine, setVoiceLine] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!event || !event.event_id || event.concierge_whisper) return;
      try {
        const cacheKey = `${event.source || "evt"}-${event.event_id}`;
        const params = new URLSearchParams({ name: event.name || event.title || event.performer || "this event" });
        const ed = [
          event.venue_name && `Held at ${event.venue_name}`,
          event.genre,
          event.performer,
          event.info,
        ].filter(Boolean).join(" · ").slice(0, 1500);
        if (ed) params.set("editorial", ed);
        if (event.venue_full_address) params.set("neighborhood", event.venue_full_address);
        if (event.city || event.city_name) params.set("city", event.city || event.city_name);
        if (event.genre) params.set("types_label", event.genre);
        const r = await fetch(`${_ENJOY_BACKEND}/api/concierge/voice/enjoy/${encodeURIComponent(cacheKey)}?${params.toString()}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.voice_line) setVoiceLine(j.voice_line);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [event?.event_id, event?.source, event?.name, event?.venue_name, event?.genre, event?.performer, event?.info, event?.concierge_whisper, event?.city, event?.city_name, event?.venue_full_address, event?.title]);

  if (!event) return null;
  const isExperience = event.kind === "experience";
  const dateLine = fmtDateRange(event.start_at, event.end_at, event.date_note, event.kind);
  const booking = event.booking || { tier: 3, ladder: [], concierge_always_present: true };
  const ladder = (booking.ladder || []).filter(
    (r) => !["vivid_seats", "stubhub", "viagogo", "seatpick", "ticombo", "gigsberg"].includes(r.kind)
  );

  // Share + Save-as-Card actions · mirror /table's RESERVATION ROUTING chrome.
  const onShare = async () => {
    const url = `${window.location.origin}/enjoy?event=${encodeURIComponent(event.event_id)}&src=${event.source}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: event.title, text: event.title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch (e) { /* user cancelled · ignore */ }
  };
  const onSaveAsCard = () => {
    // Print-to-PDF stub for now · mirrors /table's "Save as Card" pattern.
    // Full PDF generator already exists for /table; same renderer wires
    // in next pass under Roadmap P2 "QR sticker on PDF".
    window.print();
  };

  return (
    <div className="enjoy-drawer-overlay" onClick={onClose} data-testid="enjoy-drawer-overlay">
      <div className="enjoy-drawer" onClick={(e) => e.stopPropagation()} data-testid="enjoy-drawer">
        {/* EVENT ROUTING chrome · the ONLY sticky header. Title +
            provenance + badges now live inside the scrolling body so
            (a) the chrome bar's height stays predictable, (b) the body
            never tucks half-clipped content under the head section,
            and (c) the title scrolls naturally with the rest of the
            card (matches /table's drawer rhythm). */}
        <div
          className="enjoy-drawer-chrome"
          data-testid="enjoy-drawer-chrome"
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "14px 22px", borderBottom: "1px solid var(--enjoy-line)",
            background: "var(--enjoy-paper)",
            flexShrink: 0,
            position: "sticky", top: 0, zIndex: 5,
          }}
        >
          <span
            className="t-mono uppercase"
            style={{
              fontSize: 10, letterSpacing: "0.22em", color: "var(--enjoy-brass-deep)",
              fontWeight: 600,
            }}
            data-testid="enjoy-drawer-eyebrow"
          >Event Routing</span>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              data-testid="enjoy-drawer-share"
              onClick={onShare}
              className="enjoy-btn ghost"
              style={{ fontSize: 11, padding: "6px 14px", letterSpacing: "0.18em" }}
            >Share ↗</button>
            <button
              type="button"
              data-testid="enjoy-drawer-save-card"
              onClick={onSaveAsCard}
              className="enjoy-btn ghost"
              style={{ fontSize: 11, padding: "6px 14px", letterSpacing: "0.18em" }}
            >↓ Save as card</button>
            <button
              className="close"
              onClick={onClose}
              aria-label="Close"
              data-testid="enjoy-drawer-close"
              style={{
                background: "transparent", border: 0, fontSize: 18,
                cursor: "pointer", color: "var(--enjoy-ink)", padding: "0 6px",
              }}
            >✕</button>
          </div>
        </div>

        {/* Scrollable body · starts BELOW the chrome with explicit
            safe-area top padding. The title section (formerly `.head`)
            now lives in here as the first child so the body always has
            breathing space under the sticky chrome. */}
        <div className="content" data-testid="enjoy-drawer-content">
          <div
            className="enjoy-drawer-titleblock"
            data-testid="enjoy-drawer-titleblock"
            style={{ marginBottom: 22 }}
          >
            <span className="eyebrow">{event.category}{isExperience ? " · Experience" : ""}</span>
            <h2 className="serif" style={{
              fontFamily: "Fraunces, serif",
              fontSize: 26, lineHeight: 1.18,
              margin: "6px 0 0", fontWeight: 500,
              color: "var(--enjoy-ink)",
            }}>{event.title}</h2>
            {/* Provenance line · mirrors /table "PER GOOGLE · DESK CONFIRMS AT BOOKING" */}
            <p
              className="t-mono uppercase"
              data-testid="enjoy-drawer-provenance"
              style={{
                fontSize: 10, letterSpacing: "0.18em",
                color: "var(--enjoy-muted)", marginTop: 8,
                fontWeight: 500,
              }}
            >
              {event.source === "ticketmaster"
                ? "Per Ticketmaster · Desk confirms at booking"
                : event.is_curated || event.source === "curated"
                  ? "Curated by Concierge® · Desk holds the door"
                  : event.source === "predicthq"
                    ? "Per PredictHQ · the Concierge® sources directly"
                    : isExperience
                      ? "Through your Concierge® · Est 1998"
                      : "Sourced by the Concierge® · confirmed at booking"}
            </p>
            {/* Source + sale-status badges · founder ruling 16 Feb 2026
                PM-10. "Official · Ticketmaster" + "On sale now" pills
                sit directly under the title so the member sees the
                contract before the hero image loads. */}
            <DrawerSourceBadges event={event} />
          </div>
          {/* Event hero · For Signature Experiences with the new image
              hierarchy (founder ruling 16 Feb 2026 PM-11 · Workstream
              A), `hero_image_url` from the wire is used and the
              attribution renders below for `google_places` sourced
              photos. For TM/curated events the legacy `image_url` is
              used. Honest fallback: hide on load failure. */}
          {(() => {
            const heroSrc = event.hero_image_url || event.image_url;
            if (!heroSrc) return null;
            const isPlaces = event.hero_image_source_type === "google_places";
            const credit = isPlaces && Array.isArray(event.google_photo_html_attributions)
              ? event.google_photo_html_attributions[0]
              : null;
            return (
              <div style={{ position: "relative", marginBottom: 18 }}>
                <CldImg
                  src={heroSrc} w={1200} eager alt=""
                  style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", borderRadius: 4 }}
                  data-testid="enjoy-drawer-event-hero"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
                {credit && (
                  <div
                    data-testid="enjoy-drawer-hero-attribution"
                    className="t-mono"
                    style={{
                      position: "absolute", left: 8, bottom: 12,
                      fontSize: 9.5, letterSpacing: "0.04em",
                      color: "#fff",
                      background: "rgba(0, 0, 0, 0.55)",
                      padding: "3px 8px", borderRadius: 100,
                      backdropFilter: "blur(4px)",
                      maxWidth: "calc(100% - 16px)", overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                    dangerouslySetInnerHTML={{ __html: `Photo · ${credit}` }}
                  />
                )}
              </div>
            );
          })()}

          {/* ── Event details block · structured grid of Venue / Location /
                 Date / Time / Performer / Genre / Source / Price. Renders
                 only the rows that have a real value. */}
          <DrawerEventDetails event={event} />

          {/* ── Ticket status block · On sale now / Sale opens / Sale until
                 + "Official ticketing route" when TM provides a URL. */}
          <DrawerTicketStatus event={event} />

          {event.concierge_whisper ? (
            <div style={{ marginTop: 14 }}>
              <DrawerVoiceLine
                text={event.concierge_whisper}
                T={{
                  bg:    "var(--enjoy-paper)",
                  card:  "var(--enjoy-paper)",
                  ink:   "var(--enjoy-ink)",
                  ink2:  "var(--enjoy-ink2)",
                  ink3:  "var(--enjoy-ink3)",
                  line:  "var(--enjoy-line)",
                  line2: "var(--enjoy-line)",
                  brass: "var(--enjoy-brass-deep, #8B6914)",
                }}
                testid="enjoy-drawer-whisper"
              />
            </div>
          ) : voiceLine ? (
            <div style={{ marginTop: 14 }}>
              <DrawerVoiceLine
                text={voiceLine}
                T={{
                  bg:    "var(--enjoy-paper)",
                  card:  "var(--enjoy-paper)",
                  ink:   "var(--enjoy-ink)",
                  ink2:  "var(--enjoy-ink2)",
                  ink3:  "var(--enjoy-ink3)",
                  line:  "var(--enjoy-line)",
                  line2: "var(--enjoy-line)",
                  brass: "var(--enjoy-brass-deep, #8B6914)",
                }}
                testid="enjoy-drawer-concierge-voice"
              />
            </div>
          ) : null}

          {/* ── Concierge® whisper · provenance="mira_editorial" ──
              Visually distinct from desk whisper (whisper = serif italic
              with brass border-left; editorial = sans block with mono
              "Concierge® whisper" eyebrow). Member can always tell the
              desk's voice from the model's prose from the provider's
              facts. (Renamed from "Mira · editorial" per founder ruling
              15 Feb 2026 PM — the editorial layer belongs to the desk,
              not to the model that wrote it.) */}
          {event.editorial && (event.editorial.why_go || event.editorial.what_to_expect) && (
            <section
              data-testid="enjoy-drawer-editorial"
              style={{
                marginTop: 18, marginBottom: 18,
                padding: "16px 18px",
                background: "rgba(160, 122, 56, 0.06)",
                border: "1px solid rgba(160, 122, 56, 0.18)",
                borderRadius: 4,
              }}
            >
              <p
                className="t-mono uppercase"
                style={{
                  fontSize: 9, letterSpacing: "0.22em",
                  color: "var(--enjoy-brass-deep)",
                  fontFamily: "IBM Plex Mono, monospace",
                  marginBottom: 10, fontWeight: 600,
                }}
                title="Concierge® whisper · written by the Concierge®'s editorial layer from venue facts + Concierge® whisper · never invents lineup/price/date"
              >Concierge® whisper</p>
              {event.editorial.why_go && (
                <p
                  data-testid="enjoy-drawer-editorial-why-go"
                  style={{
                    fontSize: 14.5, color: "var(--enjoy-ink)",
                    lineHeight: 1.55, margin: 0,
                  }}
                >{event.editorial.why_go}</p>
              )}
              {event.editorial.what_to_expect && (
                <p
                  data-testid="enjoy-drawer-editorial-what-to-expect"
                  style={{
                    fontSize: 13.5, color: "var(--enjoy-muted)",
                    lineHeight: 1.5, margin: "8px 0 0",
                  }}
                >{event.editorial.what_to_expect}</p>
              )}
            </section>
          )}

          {/* ── Demand pulse · provenance="intent_count" · honest signal ── */}
          {event.editorial && event.editorial.demand_pulse && (
            <div
              data-testid="enjoy-drawer-demand-pulse"
              className="t-mono uppercase"
              style={{
                fontSize: 10, letterSpacing: "0.18em",
                color: "var(--enjoy-brass-deep)",
                background: "var(--enjoy-card-bg)",
                border: "1px dashed var(--enjoy-brass)",
                borderRadius: 2,
                padding: "6px 12px",
                marginBottom: 14, display: "inline-block",
              }}
              title={`Requested via Concierge® ${event.editorial.demand_pulse.count} times in the last ${event.editorial.demand_pulse.period_days} days · from lc_events_intents`}
            >
              Requested {event.editorial.demand_pulse.count}× this week
            </div>
          )}
          {/* ── Atmosphere chips · TM Discovery harvest ──────────────
              founder ruling 14 Feb 2026 PM-3: "we have ticketmaster api
              · what are the atmosphere chips we have access to though
              it · why is it not filling this in and enriching the first
              card itself". Genre · age · sale status · sale opens ·
              multi-day · ticket limit · promoter — all from TM. */}
          <AtmosphereChips event={event} />

          {/* ── Lineup · TM _embedded.attractions[] · the actual artists ── */}
          {event.attractions && event.attractions.length > 0 && (
            <section data-testid="enjoy-drawer-attractions" style={{ marginTop: 18 }}>
              <p className="t-mono uppercase"
                 style={{ fontSize: 10, letterSpacing: "0.22em",
                          color: "var(--enjoy-brass-deep)", fontWeight: 600, marginBottom: 10 }}>
                {event.attractions.length > 1 ? "Lineup" : "Performing"}
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {event.attractions.map((a, i) => (
                  <a
                    key={`${a.name}-${i}`}
                    href={a.url || undefined}
                    target={a.url ? "_blank" : undefined}
                    rel={a.url ? "noopener noreferrer" : undefined}
                    data-testid={`enjoy-drawer-attraction-${i}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 12px 8px 8px",
                      background: "var(--enjoy-paper)",
                      border: "1px solid var(--enjoy-line)",
                      borderRadius: 4, textDecoration: "none",
                      color: "var(--enjoy-ink)", cursor: a.url ? "pointer" : "default",
                    }}
                  >
                    {a.image
                      ? <CldImg src={a.image} w={72} alt={a.name}
                          style={{ width: 36, height: 36, borderRadius: 2, objectFit: "cover" }} />
                      : <div style={{ width: 36, height: 36, borderRadius: 2,
                          background: "var(--enjoy-brass-wash)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "var(--enjoy-brass)", fontWeight: 600 }}>
                          {(a.name || "?")[0]}
                        </div>
                    }
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.2 }}>{a.name}</div>
                      {(a.genre || a.sub_genre) && (
                        <div className="t-mono"
                          style={{ fontSize: 10, color: "var(--enjoy-muted)",
                                   letterSpacing: "0.08em", marginTop: 2 }}>
                          {[a.genre, a.sub_genre].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* ── Atmospheric gallery · TM images[] (distinct asset variants).
                 Founder ruling 16 Feb 2026 PM-10: alt MUST be empty so a
                 failed image never leaks "{title} 2{title} 3..." copy as
                 visible text. Also client-side dedupe by URL · backend
                 dedupes by asset id but we belt-and-brace here too. Hide
                 entire image on load failure (no broken-icon glyph). */}
          {(() => {
            const heroUrl = event.image_url;
            const seen = new Set();
            const gallery = (event.images_gallery || []).filter((u) => {
              if (!u || u === heroUrl) return false;
              if (seen.has(u)) return false;
              seen.add(u);
              return true;
            });
            if (gallery.length < 2) return null;
            return (
              <section data-testid="enjoy-drawer-gallery" style={{ marginTop: 18 }}>
                <div
                  style={{
                    display: "flex", gap: 10, overflowX: "auto",
                    paddingBottom: 8, scrollSnapType: "x mandatory",
                  }}
                >
                  {gallery.map((u, i) => (
                    <CldImg
                      key={u} src={u} w={480} alt=""
                      data-testid={`enjoy-drawer-gallery-${i}`}
                      style={{
                        height: 130, minWidth: 220, objectFit: "cover",
                        borderRadius: 4, flexShrink: 0,
                        scrollSnapAlign: "start",
                      }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ))}
                </div>
              </section>
            );
          })()}

          {/* ── Editorial · provider's editorial blurb · founder pin
                15 Feb 2026 PM-3 · the eyebrow MUST reflect the ACTUAL
                source. Mislabelling PredictHQ data as "Per Ticketmaster"
                is a doctrine breach. */}
          {event.info && (
            <section data-testid="enjoy-drawer-tm-info"
              style={{ marginTop: 18, padding: "14px 18px",
                       background: "var(--enjoy-paper)",
                       border: "1px solid var(--enjoy-line)", borderRadius: 4 }}>
              <p className="t-mono uppercase"
                 data-testid="enjoy-drawer-info-source"
                 style={{ fontSize: 10, letterSpacing: "0.22em",
                          color: "var(--enjoy-muted)", marginBottom: 8, fontWeight: 600 }}>
                {event.source === "ticketmaster"
                  ? "Per Ticketmaster"
                  : event.source === "predicthq"
                    ? "Per PredictHQ"
                    : event.source === "curated" || event.is_curated
                      ? "Per Concierge® desk"
                      : `Per ${event.source}`}
              </p>
              <p style={{ fontSize: 13.5, color: "var(--enjoy-ink)", lineHeight: 1.55, margin: 0 }}>
                {event.info}
              </p>
            </section>
          )}

          {/* ── Please note · TM `pleaseNote` (doors, no re-entry, etc.) ── */}
          {event.please_note && (
            <section data-testid="enjoy-drawer-please-note"
              style={{ marginTop: 12, padding: "10px 14px",
                       background: "rgba(160,122,56,0.06)",
                       border: "1px solid rgba(160,122,56,0.2)",
                       borderRadius: 3 }}>
              <p className="t-mono uppercase"
                 style={{ fontSize: 9.5, letterSpacing: "0.22em",
                          color: "var(--enjoy-brass-deep)", marginBottom: 4, fontWeight: 600 }}>
                Please note
              </p>
              <p style={{ fontSize: 13, color: "var(--enjoy-ink)", lineHeight: 1.5, margin: 0 }}>
                {event.please_note}
              </p>
            </section>
          )}

          {/* ── Venue facts · accessibility / parking / rules / box office ── */}
          {(event.accessibility_notes || event.parking_notes || event.venue_general_rule ||
            event.venue_full_address || event.venue_box_office) && (
            <section data-testid="enjoy-drawer-venue-info" style={{ marginTop: 16 }}>
              <p className="t-mono uppercase"
                 style={{ fontSize: 10, letterSpacing: "0.22em",
                          color: "var(--enjoy-brass-deep)", marginBottom: 8, fontWeight: 600 }}>
                Venue
              </p>
              {event.venue_full_address && (
                <p style={{ fontSize: 13, color: "var(--enjoy-ink)", margin: "0 0 6px", lineHeight: 1.5 }}>
                  <strong>Address · </strong>{event.venue_full_address}
                </p>
              )}
              {event.venue_box_office && (
                <p style={{ fontSize: 13, color: "var(--enjoy-ink)", margin: "0 0 6px", lineHeight: 1.5 }}>
                  <strong>Box office · </strong>{event.venue_box_office}
                </p>
              )}
              {event.accessibility_notes && (
                <p style={{ fontSize: 13, color: "var(--enjoy-ink)", margin: "0 0 6px", lineHeight: 1.5 }}>
                  <strong>Accessibility · </strong>{event.accessibility_notes}
                </p>
              )}
              {event.parking_notes && (
                <p style={{ fontSize: 13, color: "var(--enjoy-ink)", margin: "0 0 6px", lineHeight: 1.5 }}>
                  <strong>Parking · </strong>{event.parking_notes}
                </p>
              )}
              {event.venue_general_rule && (
                <p style={{ fontSize: 13, color: "var(--enjoy-muted)", margin: 0, lineHeight: 1.5 }}>
                  {event.venue_general_rule}
                </p>
              )}
            </section>
          )}

          {/* Residual meta-grid · only the fields NOT in DrawerEventDetails
              (Promoter / Season / Collection). Venue / Location / Date /
              Time / Genre / Price / Source already render in the
              structured details block above. */}
          {(event.promoter || event.season_category || event.collection) && (
            <dl className="meta-grid" data-testid="enjoy-drawer-meta">
              {event.promoter && (<><dt>Promoter</dt><dd>{event.promoter}</dd></>)}
              {event.season_category && (<><dt>Season</dt><dd>{event.season_category}</dd></>)}
              {event.collection && (<><dt>Collection</dt><dd>{event.collection}</dd></>)}
            </dl>
          )}

          {/* ─── Venue section · Google Places · clearly labelled ─── */}
          {event.venue && (event.venue.photo_url || event.venue.lat) && (
            <VenueLayer venue={event.venue} fallback_name={event.venue_name} />
          )}
        </div>

        {/* ─── Booking ladder + Concierge® parallel sibling ─── */}
        <div className="actions" data-testid="enjoy-drawer-actions">
          {ladder.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
              {ladder.map((r, i) => {
                const isNote = r.is_note;
                const isFind = r.is_search;
                // Founder copy ruling 14 Feb 2026 PM-5:
                //   Tier-1 verified primary: "Straight to the box office ↗"
                //     sub: official · {Platform}
                //   Sibling: "Request your Concierge®" (replaces "Request via")
                //
                // The pair reads: Straight to the box office / Let your
                // Concierge® — self-serve or hand-it-over, both sounding
                // like the desk offering the choice.
                const isPrimaryBook = i === 0 && !isNote && !isFind;
                const platformLabel = r.label || r.kind;
                // K1.2 (14 Jun 2026 evening · founder ruling):
                // Member-facing verbs drop the supplier name. The
                // primary keeps the editorial "Straight to the box
                // office" line · non-primary "Book on Ticketmaster"
                // / "Book on DICE" etc collapse to a clean "Book"
                // verb. Platform identity stays in the URL host and
                // the `data-rail-kind` attribute below.
                const verb = isNote ? "Read more"
                           : isFind ? "Find tickets"
                           : isPrimaryBook ? "Straight to the box office"
                           : "Book";
                const testid = isNote ? `enjoy-drawer-route-note-${r.kind}`
                               : isFind ? `enjoy-drawer-route-find-${r.kind}`
                               : `enjoy-drawer-route-book-${r.kind}`;
                // Trail · only on verified Book buttons. Find/Read-more
                // have nothing to verify; Concierge's trust signal is
                // the desk, not a timestamp. (founder pin 14 Feb 2026 PM)
                const trail = (!isNote && !isFind && r.verified_at)
                  ? verifiedTrail(r.verified_at) : null;
                return (
                  <div key={r.kind + i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className={isPrimaryBook ? "enjoy-btn" : "enjoy-btn ghost"}
                      onClick={() => onBookDirect(event, r)}
                      data-testid={testid}
                      data-rail-kind={r.kind || undefined}
                      data-rail-label={platformLabel || undefined}
                      style={{ justifyContent: "center" }}
                    >
                      {verb} ↗
                    </a>
                    {/* K1.2 (14 Jun 2026 evening · founder ruling):
                        the "official · Ticketmaster" sub-label is
                        REMOVED from the drawer primary too. Platform
                        identity stays in `data-rail-kind` on the link
                        below. */}
                    {trail && (
                      <span
                        data-testid={`enjoy-drawer-route-trail-${r.kind}`}
                        className="t-mono"
                        style={{
                          fontSize: 10, letterSpacing: "0.06em",
                          color: "var(--enjoy-muted)",
                          textAlign: "center", paddingTop: 1,
                          fontFamily: "IBM Plex Mono, monospace",
                        }}
                      >{trail}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Verified access routes · founder ruling 16 Feb 2026 PM-9 ──
                Replaces the multi-supplier marketplace ("via Keith Prowse",
                "via Sportsworld", "via F1 Experiences" with Open buttons)
                with ONE clean member-facing access block. Doctrine: the
                member should not feel they're choosing a broker · the desk
                holds the door.

                · API-direct rows (Ticketmaster) keep the existing primary
                  ladder and "Find on Ticketmaster" CTA — that's official and
                  fine to name.
                · Concierge-arranged rows (Cornucopia / official hospitality /
                  debenture / allocation partners / Signature) get a single
                  block with 4 trust pills + "Request via Concierge®" CTA.
                · Supplier list (the old marketplace) moves into a collapsed
                  "Desk routing details" block · role-gated to admin/desk.
          */}
          {Array.isArray(event.hospitality_rails) && event.hospitality_rails.length > 0 && (
            <AccessRouteBlock
              event={event}
              hospitalityRails={event.hospitality_rails}
              onRequestConcierge={onRequestConcierge}
              isAdmin={isAdminViewer}
            />
          )}

          <button
            className="enjoy-btn ghost"
            onClick={() => onRequestConcierge(event)}
            data-testid="enjoy-drawer-request-concierge"
            style={{ width: "100%", justifyContent: "center",
                     borderColor: "var(--enjoy-brass)", color: "var(--enjoy-brass-deep)" }}
          >
            Request your Concierge®
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Venue section · provenance = google_places ───────────────────────── */
function VenueLayer({ venue, fallback_name }) {
  const name = venue.name || fallback_name;
  return (
    <section
      data-testid="enjoy-drawer-venue"
      style={{
        marginTop: 26, paddingTop: 22,
        borderTop: "1px solid var(--enjoy-line)",
      }}
    >
      <p
        className="eyebrow"
        style={{
          fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
          color: "var(--enjoy-muted)", fontFamily: "IBM Plex Mono, monospace",
          marginBottom: 12,
        }}
      >Venue · per Concierge®</p>
      <div style={{ display: "flex", gap: 14, alignItems: "stretch" }}>
        {venue.photo_url && (
          <div style={{ flex: "0 0 130px", height: 130, overflow: "hidden",
                        borderRadius: 4, border: "1px solid var(--enjoy-line)" }}>
            <CldImg
              src={venue.photo_url}
              w={280}
              alt={`Venue · ${name}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              data-testid="enjoy-drawer-venue-photo"
            />
          </div>
        )}
        <div style={{ flex: 1, display: "flex", flexDirection: "column",
                      justifyContent: "center", gap: 6 }}>
          <p
            data-testid="enjoy-drawer-venue-name"
            className="serif"
            style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.3, margin: 0,
                     color: "var(--enjoy-ink)" }}
          >{name || "Venue"}</p>
          {venue.lat && venue.lng && (
            <a
              data-testid="enjoy-drawer-venue-map"
              href={`https://www.google.com/maps/search/?api=1&query=${venue.lat},${venue.lng}${venue.place_id ? `&query_place_id=${venue.place_id}` : ""}`}
              target="_blank" rel="noreferrer"
              className="t-mono uppercase"
              style={{
                fontSize: 10, letterSpacing: "0.18em",
                color: "var(--enjoy-brass-deep)", textDecoration: "none",
                fontFamily: "IBM Plex Mono, monospace", fontWeight: 600,
              }}
            >Open in Maps ↗</a>
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Sign-in modal (warm gatekeeping) ─────────────────────────────────── */
function SignInGate({ onAuthed, onClose }) {
  const [card, setCard] = useState("");
  const [last, setLast] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!card.trim() || !last.trim()) {
      setError("Please enter both your card number and last name.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await clubApi.authMember({ card: card.trim(), last: last.trim() });
      const m = r.member;
      storeMember(m);
      onAuthed(m);
    } catch (e2) {
      setError(e2.message || "Couldn't sign you in. Try again.");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="enjoy-modal-overlay" onClick={onClose} data-testid="enjoy-signin-overlay">
      <div className="enjoy-modal" onClick={(e) => e.stopPropagation()} data-testid="enjoy-signin-modal">
        <div className="head">
          <span className="eyebrow">Members area</span>
          <h3 className="serif">Sign in to send your request</h3>
        </div>
        <form onSubmit={submit}>
          <div className="body">
            <p style={{ fontSize: 14, color: "var(--enjoy-muted)", marginTop: 0, marginBottom: 18 }}>
              Concierge® requests are reserved for members. New here? <Link to="/club/signup" style={{ color: "var(--enjoy-brass-deep)", fontWeight: 600 }}>Apply for membership →</Link>
            </p>
            {error && <div className="enjoy-error" data-testid="enjoy-signin-error">{error}</div>}
            <div className="field">
              <label htmlFor="enjoy-card">Membership card number</label>
              <input id="enjoy-card" autoFocus value={card} onChange={(e) => setCard(e.target.value)}
                placeholder="LC-2026-0001" data-testid="enjoy-signin-card" />
            </div>
            <div className="field">
              <label htmlFor="enjoy-last">Last name</label>
              <input id="enjoy-last" value={last} onChange={(e) => setLast(e.target.value)}
                data-testid="enjoy-signin-last" />
            </div>
          </div>
          <div className="actions">
            <button type="button" className="enjoy-btn ghost" onClick={onClose} data-testid="enjoy-signin-cancel">Cancel</button>
            <button type="submit" className="enjoy-btn" disabled={submitting} data-testid="enjoy-signin-submit">
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Concierge Request modal ──────────────────────────────────────────── */
function RequestModal({ event, member, onClose, onSubmitted }) {
  const isExperience = event?.kind === "experience";
  // "Generic ask" mode (founder ruling 27 Jun 2026 night) — mirrors
  // /table's open-ask flow. When the member arrives via the /enjoy hero
  // "Ask the Concierge®" CTA there's no specific event chosen yet; the
  // form must collect the event/experience name + city explicitly so
  // the desk has a brief, not just a vibe.
  const isGenericAsk = !!event?._is_generic;
  const [tickets, setTickets] = useState(2);
  const [seating, setSeating] = useState("");
  const [partySize, setPartySize] = useState(2);
  const [preferredSeason, setPreferredSeason] = useState("");
  const [occasion, setOccasion] = useState("");
  const [preferences, setPreferences] = useState("");
  const embedMember = useEmbedMember();
  // Guest contact · mirrors /table ConciergeRequestPayload. Pre-filled
  // when a /club member is signed in, or via useEmbedMember when
  // rendered inside the Corporate Portal drawer / Aurelion embed.
  const [memberName, setMemberName] = useState(
    (member ? `${member.first || ""} ${member.last || ""}`.trim() : "")
    || embedMember?.name || ""
  );
  const [memberEmail, setMemberEmail] = useState(member?.email || embedMember?.email || "");
  const [memberPhone, setMemberPhone] = useState(member?.phone || embedMember?.phone || "");
  // Generic-ask only · editable event/experience name + city.
  const [askEventTitle, setAskEventTitle] = useState("");
  const [askEventCity, setAskEventCity] = useState(event?.city || "");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isGuest = !member;

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (isGenericAsk && !askEventTitle.trim()) {
      setError("What would you like tickets/access to? (Name + city helps us most.)");
      return;
    }
    if (isGuest) {
      if (!memberName.trim() || (!memberEmail.trim() && !memberPhone.trim())) {
        setError("Tell us your name and either an email or phone — so the Concierge® can come back to you.");
        return;
      }
    }
    setSubmitting(true);
    try {
      // Resolve final event snapshot. In generic-ask mode we overlay
      // the member-typed values on top of the placeholder event so the
      // backend + Zoho ticket carries the real ask, not "Generic Concierge® request".
      const resolvedSnapshot = isGenericAsk
        ? {
            ...event,
            title: askEventTitle.trim(),
            city: askEventCity.trim() || event.city || undefined,
          }
        : event;
      const payload = {
        source: event.source,
        event_id: event.event_id,
        event_snapshot: resolvedSnapshot,
        preferences,
        member_name: memberName.trim() || undefined,
        member_email: memberEmail.trim() || undefined,
        member_phone: memberPhone.trim() || undefined,
      };
      if (isExperience) {
        payload.party_size = partySize;
        payload.preferred_season = preferredSeason;
        payload.occasion = occasion;
      } else {
        payload.tickets = tickets;
        payload.seating = seating;
      }
      const r = await enjoy.requestViaConcierge(payload, member?.card_no);
      enjoy.logIntent({
        type: "concierge_request",
        source: event.source,
        event_id: event.event_id,
        event_snapshot: event,
      });
      onSubmitted(r);
    } catch (e2) {
      setError(e2.message || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  }

  return (
    <div className="enjoy-modal-overlay" onClick={onClose} data-testid="enjoy-request-overlay">
      <div className="enjoy-modal" onClick={(e) => e.stopPropagation()} data-testid="enjoy-request-modal">
        <div className="head">
          <span className="eyebrow">{event?.category} {isExperience ? "· Experience" : ""}</span>
          <h3 className="serif">{isGenericAsk ? "Tell us what you'd love" : "Request via Concierge®"}</h3>
          <p style={{ fontSize: 13, color: "var(--enjoy-muted)", margin: "8px 0 0" }}>
            {isGenericAsk
              ? "We'll find the right door. Name the event or experience, city helps."
              : event?.title}
          </p>
        </div>
        <form onSubmit={submit}>
          <div className="body">
            {error && <div className="enjoy-error" data-testid="enjoy-request-error">{error}</div>}

            {/* Generic-ask · event/experience-of-choice capture.
                Mirrors /table's "Your table of choice" flow. Only shown
                when the member arrived via the /enjoy hero "Ask the
                Concierge®" CTA with no specific event in hand. */}
            {isGenericAsk && (
              <>
                <div className="field">
                  <label htmlFor="enjoy-ask-event-title">
                    {isExperience ? "Experience of choice" : "Event of choice"}
                  </label>
                  <input
                    id="enjoy-ask-event-title"
                    value={askEventTitle}
                    onChange={(e) => setAskEventTitle(e.target.value)}
                    placeholder={isExperience
                      ? "e.g. Hot-air balloon over Cappadocia, paragliding in Bir"
                      : "e.g. Coldplay Mumbai, Wimbledon finals, Magnetic Fields"}
                    autoFocus
                    data-testid="enjoy-request-ask-event-title"
                  />
                </div>
                <div className="field">
                  <label htmlFor="enjoy-ask-event-city">City (helps a lot)</label>
                  <input
                    id="enjoy-ask-event-city"
                    value={askEventCity}
                    onChange={(e) => setAskEventCity(e.target.value)}
                    placeholder="e.g. Mumbai · London · Paris"
                    data-testid="enjoy-request-ask-event-city"
                  />
                </div>
              </>
            )}

            {/* Guest contact block · only when no /club session. Member-
                signed-in path hides these because the card carries them. */}
            {isGuest && (
              <>
                <div className="field">
                  <label htmlFor="enjoy-guest-name">Your name</label>
                  <input id="enjoy-guest-name" value={memberName}
                    onChange={(e) => setMemberName(e.target.value)}
                    placeholder="First and last"
                    data-testid="enjoy-request-guest-name" />
                </div>
                <div className="field" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label htmlFor="enjoy-guest-email">Email</label>
                    <input id="enjoy-guest-email" type="email" value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      placeholder="you@domain.com"
                      data-testid="enjoy-request-guest-email" />
                  </div>
                  <div>
                    <label htmlFor="enjoy-guest-phone">Phone / WhatsApp</label>
                    <input id="enjoy-guest-phone" value={memberPhone}
                      onChange={(e) => setMemberPhone(e.target.value)}
                      placeholder="+91 9876 …"
                      data-testid="enjoy-request-guest-phone" />
                  </div>
                </div>
              </>
            )}

            {isExperience ? (
              <>
                <div className="field">
                  <label>Party size</label>
                  <input type="number" min="1" max="20" value={partySize}
                    onChange={(e) => setPartySize(parseInt(e.target.value || "1", 10))}
                    data-testid="enjoy-request-party-size" />
                </div>
                <div className="field">
                  <label>Preferred season / dates</label>
                  <input value={preferredSeason} onChange={(e) => setPreferredSeason(e.target.value)}
                    placeholder="e.g. December · Christmas week"
                    data-testid="enjoy-request-preferred-season" />
                </div>
                <div className="field">
                  <label>Occasion (optional)</label>
                  <input value={occasion} onChange={(e) => setOccasion(e.target.value)}
                    placeholder="anniversary, birthday, family trip…"
                    data-testid="enjoy-request-occasion" />
                </div>
              </>
            ) : (
              <>
                <div className="field">
                  <label>Tickets</label>
                  <input type="number" min="1" max="20" value={tickets}
                    onChange={(e) => setTickets(parseInt(e.target.value || "1", 10))}
                    data-testid="enjoy-request-tickets" />
                </div>
                <div className="field">
                  <label>Seating preference</label>
                  <input value={seating} onChange={(e) => setSeating(e.target.value)}
                    placeholder="e.g. floor seats · VIP · suite"
                    data-testid="enjoy-request-seating" />
                </div>
              </>
            )}
            <div className="field">
              <label>Anything else?</label>
              <textarea rows={3} value={preferences} onChange={(e) => setPreferences(e.target.value)}
                placeholder="Date flexibility, special access, dietary needs…"
                data-testid="enjoy-request-preferences" />
            </div>
          </div>
          <div className="actions">
            <button type="button" className="enjoy-btn ghost" onClick={onClose}
              data-testid="enjoy-request-cancel">Cancel</button>
            <button type="submit" className="enjoy-btn" disabled={submitting}
              data-testid="enjoy-request-submit">
              {submitting ? "Sending…" : "Send to Concierge®"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Empty state · founder ruling 16 Feb 2026 PM-3 ─────────────────────
 * Doctrine: the concierge is "there for them, anytime, anywhere — our job
 * is to find other options." So when the public platforms have no door
 * for the brief, we don&apos;t say "nothing here." We surface the desk
 * actively: echo the brief, accept a one-line note, route it to
 * /api/enjoy/request. The full request form remains one tap away for
 * the longer brief. Doctrine pin 16 Feb 2026 PM-7: the desk is always
 * the subject. We never name the platform that fell short.
 */
function EmptyState({ onTellUs, query, city, categoryLabel, member, onQuickSend }) {
  const isGuest = !member;
  const embedMember = useEmbedMember();
  const [note, setNote] = React.useState("");
  const [name, setName] = React.useState(
    (member ? `${member.first || ""} ${member.last || ""}`.trim() : "")
    || embedMember?.name || ""
  );
  const [email, setEmail] = React.useState(member?.email || embedMember?.email || "");
  const [phone, setPhone] = React.useState(member?.phone || embedMember?.phone || "");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(null);
  const [contactErr, setContactErr] = React.useState(null);

  // Build a human-readable echo of what the member was after. Resilient to
  // missing pieces — never echoes a bare "in undefined".
  const briefBits = [];
  if (query) briefBits.push(<><strong style={{ color: "var(--t-ink)" }}>{query}</strong></>);
  else if (categoryLabel && categoryLabel.toLowerCase() !== "all") briefBits.push(<><strong style={{ color: "var(--t-ink)" }}>{categoryLabel.toLowerCase()}</strong></>);
  if (city) briefBits.push(<>in <strong style={{ color: "var(--t-ink)" }}>{city}</strong></>);

  async function handleQuickSend(e) {
    e.preventDefault();
    if (!onQuickSend || sending) return;
    setContactErr(null);
    if (isGuest && (!name.trim() || (!email.trim() && !phone.trim()))) {
      setContactErr("Your name and either an email or phone — so the Concierge® can come back to you.");
      return;
    }
    setSending(true);
    try {
      const resp = await onQuickSend({
        note: note.trim(),
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      if (resp && (resp.display_ref || resp.intent_id)) {
        setSent(resp.display_ref || resp.intent_id);
        setNote("");
      } else {
        setSent("received");
      }
    } catch (_e) {
      setSent("error");
    } finally {
      setSending(false);
    }
  }

  const inputStyle = {
    width: "100%", boxSizing: "border-box",
    padding: "10px 12px",
    border: "1px solid var(--t-line2)", borderRadius: 9,
    background: "var(--t-paper)", color: "var(--t-ink)",
    fontFamily: "inherit", fontSize: 14, lineHeight: 1.4,
    outline: "none",
  };

  return (
    <div
      data-testid="enjoy-empty-state"
      style={{
        textAlign: "left", padding: "44px 36px 36px",
        border: "1px solid var(--t-line2)", borderRadius: 16,
        background: "var(--t-paper2)", marginTop: 8,
        maxWidth: 720, marginLeft: "auto", marginRight: "auto",
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
        color: "var(--t-brass)", marginBottom: 14,
      }}>
        <span
          title="Concierge® on duty"
          aria-label="Concierge® on duty"
          style={{
            width: 8, height: 8, borderRadius: "50%",
            background: "#3a9a64", boxShadow: "0 0 0 3px rgba(58,154,100,.18)",
          }}
        />
      </div>

      <h3
        className="t-serif"
        style={{ fontSize: 28, lineHeight: 1.12, margin: 0, color: "var(--t-ink)" }}
        data-testid="enjoy-empty-headline"
      >
        This one&apos;s{" "}
        <span style={{ color: "var(--t-brass)", fontStyle: "italic" }}>
          ours to arrange.
        </span>
      </h3>

      <p style={{ color: "var(--t-ink2)", fontSize: 15, margin: "16px 0 4px", lineHeight: 1.55 }}>
        {briefBits.length > 0 ? (
          <>
            {briefBits.map((b, i) => (
              <React.Fragment key={`brief-bit-${i}`}>
                {i > 0 && " "}
                {b}
              </React.Fragment>
            ))}{" "}
            is one of those nights that lives off the public platforms —
            sold direct, by invitation, or through relationships the Concierge®
            has spent years building. That&apos;s exactly what we&apos;re here for.
          </>
        ) : (
          <>
            Some nights live off the public platforms — sold direct, by
            invitation, or through relationships the Concierge® has spent years
            building. That&apos;s exactly what we&apos;re here for.
          </>
        )}
      </p>

      <p style={{ color: "var(--t-ink3)", fontSize: 13.5, margin: "10px 0 22px", lineHeight: 1.5 }}>
        Tell us a line — your dates, your party, anything specific — and we&apos;ll
        come back with the door. Or, just as honestly, tell you if there isn&apos;t one.
      </p>

      {sent ? (
        <div
          data-testid="enjoy-empty-sent"
          style={{
            padding: "14px 16px", borderRadius: 10,
            background: "rgba(58,154,100,.10)", border: "1px solid rgba(58,154,100,.35)",
            color: "var(--t-ink)", fontSize: 14,
          }}
        >
          {sent === "error" ? (
            <>Couldn&apos;t reach the Concierge® just now. <button
              onClick={() => setSent(null)}
              data-testid="enjoy-empty-retry"
              style={{ background: "none", border: "none", color: "var(--t-brass)", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: 14 }}
            >Try again</button> or hit Ask your concierge for the full brief.</>
          ) : (
            <>
              <strong>Brief received{sent !== "received" ? ` · ${sent}` : ""}.</strong>{" "}
              A concierge will write back within the working day — direct, or with the Concierge®&apos;s alternatives.
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleQuickSend} data-testid="enjoy-empty-form">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="enjoy-empty-note"
            rows={3}
            maxLength={500}
            placeholder={query
              ? `e.g. "2 of us for ${query}, weekend in June if possible"`
              : "e.g. \"2 of us, Berlin, weekend in June — anything jazz or late-night\""}
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "12px 14px",
              border: "1px solid var(--t-line2)", borderRadius: 10,
              background: "var(--t-paper)", color: "var(--t-ink)",
              fontFamily: "inherit", fontSize: 14.5, lineHeight: 1.5,
              resize: "vertical", minHeight: 84,
              outline: "none",
            }}
          />

          {isGuest && (
            <div
              data-testid="enjoy-empty-guest-fields"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginTop: 10,
              }}
            >
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="enjoy-empty-name"
                style={{ ...inputStyle, gridColumn: "1 / -1" }}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                data-testid="enjoy-empty-email"
                style={inputStyle}
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (or WhatsApp)"
                data-testid="enjoy-empty-phone"
                style={inputStyle}
              />
            </div>
          )}

          {contactErr && (
            <p
              data-testid="enjoy-empty-contact-err"
              style={{ color: "#b8410a", fontSize: 13, margin: "10px 0 0" }}
            >
              {contactErr}
            </p>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="submit"
              className="enjoy-btn"
              disabled={sending}
              data-testid="enjoy-empty-quick-send"
              style={{ minWidth: 180 }}
            >
              {sending ? "Sending…" : "Send to the Concierge®"}
            </button>
            <button
              type="button"
              onClick={onTellUs}
              data-testid="enjoy-empty-tell-us"
              style={{
                background: "none", border: "none", padding: "6px 8px",
                color: "var(--t-ink2)", fontSize: 13.5, cursor: "pointer",
                textDecoration: "underline", textUnderlineOffset: 3,
              }}
            >
              Or open the full brief
            </button>
          </div>
          <p style={{ color: "var(--t-ink3)", fontSize: 12, margin: "12px 0 0" }}>
            Anytime. Anywhere. The Concierge® holds the door.
          </p>
        </form>
      )}
    </div>
  );
}

/* ── /enjoy chip-doors · founder ruling 15 Feb 2026 PM-4 ──────────────
 * Lead with what the member wants — Music Shows, Comedy Shows, Kids,
 * Exhibitions — never with the supplier name. The supplier (BookMyShow /
 * District / Platinumlist) is plumbing. Each chip opens the right shop's
 * city-aware category page silently in a new tab.
 *
 * Replaces the previous platform-named rows ("Find on BookMyShow / Find on
 * District") and the back-of-house "Direct doors to..." copy.
 */
function CategoryDoors({ doors, resolution, city, countryNames, onConciergeRequest }) {
  if (!doors || !doors.length) return null;

  // Founder ruling 15 Feb 2026 PM-9 · the member must NEVER see a raw
  // 2-letter ISO code. Resolution order:
  //   1. Resolved city from the parse (resolution.city → e.g. "Bangalore")
  //   2. Chip-city selection
  //   3. Humanised country name from the desk-curated map
  //   4. Honest fallback "your city"
  const resolvedCity = resolution && resolution.city;
  const countryCode = resolution && resolution.country_used;
  const humanCountry = countryCode && countryNames ? countryNames[countryCode] : null;
  const titleCity = resolvedCity || city || humanCountry || "your city";

  const handleTap = (door) => {
    try {
      enjoy.logIntent({
        type: "find_rail_tap",
        source: "category_door",
        event_id: `_door_${door.key}`,
        event_snapshot: {
          category_key: door.key,
          category_label: door.label,
          category_url: door.url,
          city: city || null,
          country: resolution && resolution.country_used,
        },
        notes: `door=${door.key} via=${door.via} platform=${door.platform_kind}`,
      });
    } catch (e) { /* never block */ }
  };

  return (
    <section
      className="enjoy-rails"
      data-testid="enjoy-category-doors"
      data-resolution-source={resolution ? resolution.source : "unknown"}
    >
      <header className="enjoy-rails-head">
        <div>
          <p
            className="enjoy-rails-eyebrow"
            data-testid="enjoy-category-doors-eyebrow"
          >On the ground</p>
          <h3
            className="enjoy-rails-title"
            data-testid="enjoy-category-doors-title"
          >
            What are you in the mood for in {titleCity}?
          </h3>
        </div>
      </header>

      <div
        data-testid="enjoy-category-doors-list"
        style={{
          display: "flex", flexWrap: "wrap",
          gap: 10, marginTop: 18, marginBottom: 22,
        }}
      >
        {doors.map((door) => (
          <a
            key={door.key}
            href={door.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleTap(door)}
            data-testid={`enjoy-category-door-${door.key}`}
            data-door-via={door.via}
            style={{
              display: "inline-flex", alignItems: "center",
              padding: "8px 16px",
              borderRadius: 999,
              border: "1px solid var(--enjoy-line)",
              background: "var(--enjoy-paper)",
              color: "var(--enjoy-ink)",
              fontSize: 13, fontWeight: 500,
              textDecoration: "none",
              transition: "background 140ms ease, border-color 140ms ease",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--enjoy-brass-wash, rgba(160,122,56,0.10))";
              e.currentTarget.style.borderColor = "var(--enjoy-brass, #8b6914)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--enjoy-paper)";
              e.currentTarget.style.borderColor = "var(--enjoy-line)";
            }}
          >
            {door.label}
          </a>
        ))}
      </div>

      {/* Concierge® sibling · always parallel to the chips, never below
          a "fallback" frame. Member always has the desk-route option. */}
      <div className="enjoy-rails-sibling">
        <p className="enjoy-rails-sibling-copy">
          Or tell us what you&apos;re after and the Concierge® will find it.
        </p>
        <button
          type="button"
          className="enjoy-btn ghost"
          onClick={onConciergeRequest}
          data-testid="enjoy-category-doors-concierge-cta"
          style={{ alignSelf: "flex-start" }}
        >
          Request via Concierge®
        </button>
      </div>
    </section>
  );
}

/* ── Find-rails · city-aware platform deep-links ─────────────────────── *
 * Founder ruling 14 Feb 2026 PM: when structured results are sparse
 * (≤2), surface the city's primary platforms (BookMyShow Mumbai,
 * Platinumlist Dubai, DICE+Skiddle London, etc.) as deep-search rails.
 *
 * Strict anti-fabrication: we hand off to the platform's filtered city
 * page · we never republish their listings, posters or prices on our
 * card. Concierge® is the parallel sibling on every rail set.
 */
function FindRails({ rails, resolution, city, category, onConciergeRequest }) {
  if (!rails || !rails.length) return null;

  const titleCity = city ? city : (resolution && resolution.country_used) || "your city";
  const eyebrow = resolution && resolution.source === "city_override"
    ? "Curated for this city"
    : resolution && resolution.source === "global_fallback"
      ? "Across the web"
      : "On the ground";

  const handleRailTap = (rail) => {
    try {
      // Fire-and-forget intent log · tracks which rails earn their place.
      enjoy.logIntent({
        type: "find_rail_tap",
        source: "find_rail",
        event_id: `_rail_${rail.kind}_${rail.via}`,
        event_snapshot: {
          rail_kind: rail.kind,
          rail_label: rail.label,
          rail_via: rail.via,
          rail_url: rail.url,
          city: city || null,
          country: resolution && resolution.country_used,
          category: resolution && resolution.category,
        },
        notes: `via=${rail.via}`,
      });
    } catch (e) { /* never block */ }
  };

  return (
    <section
      className="enjoy-rails"
      data-testid="enjoy-find-rails"
      data-resolution-source={resolution ? resolution.source : "unknown"}
      data-city-override={resolution && resolution.city_override_applied ? "true" : "false"}
    >
      <header className="enjoy-rails-head">
        <div>
          <p className="enjoy-rails-eyebrow" data-testid="enjoy-rails-eyebrow">{eyebrow}</p>
          <h3 className="enjoy-rails-title" data-testid="enjoy-rails-title">
            Find more in {titleCity}
          </h3>
          <p className="enjoy-rails-note">
            Direct doors to {titleCity}&apos;s ticketing platforms — we hand off,
            they keep their listings. Tell us if a route doesn&apos;t open
            and the Concierge® will source it directly.
          </p>
        </div>
      </header>

      <div className="enjoy-rails-list">
        {rails.map((rail) => {
          if (rail.concierge_primary) {
            return (
              <button
                key={`${rail.kind}-${rail.via}`}
                type="button"
                className="enjoy-rail-row concierge-primary"
                data-testid={`enjoy-rail-${rail.kind}`}
                data-via={rail.via}
                onClick={() => {
                  handleRailTap(rail);
                  if (onConciergeRequest) onConciergeRequest();
                }}
              >
                <span className="enjoy-rail-label">{rail.label}</span>
                <span className="enjoy-rail-meta">
                  {rail.via === "city_override" ? "Curated route" : "Concierge®"}
                </span>
                <span className="enjoy-rail-arrow">→</span>
              </button>
            );
          }
          return (
            <a
              key={`${rail.kind}-${rail.via}`}
              href={rail.url}
              target="_blank"
              rel="noopener noreferrer"
              className="enjoy-rail-row"
              data-testid={`enjoy-rail-${rail.kind}`}
              data-via={rail.via}
              onClick={() => handleRailTap(rail)}
            >
              <span className="enjoy-rail-label">{rail.label} · all of {titleCity}</span>
              <span className="enjoy-rail-meta">
                {rail.via === "city_override" ? "Curated" :
                 rail.via === "global_fallback" ? "Global" : "Open in {city}".replace("{city}", titleCity)}
              </span>
              <span className="enjoy-rail-arrow">↗</span>
            </a>
          );
        })}
      </div>

      {/* Concierge® sibling · always alongside, never below a "fallback" frame */}
      {!rails.some((r) => r.concierge_primary) && (
        <div className="enjoy-rails-sibling">
          <p className="enjoy-rails-sibling-copy">
            Or skip the search — tell us what you&apos;re after and the Concierge®
            will source it directly. Same network, faster route.
          </p>
          <button
            type="button"
            className="enjoy-btn ghost"
            onClick={onConciergeRequest}
            data-testid="enjoy-rails-concierge-cta"
            style={{ alignSelf: "flex-start" }}
          >
            Request via Concierge®
          </button>
        </div>
      )}
    </section>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 * Search-first redesign · founder ruling 16 Feb 2026 PM-8
 *
 * Replaces the legacy chip-bank-first UI with a clean search-first hero.
 * All filters move behind a single "Refine search" button that opens a
 * staged drawer · defaults are invisible, active chips only show AFTER
 * Apply, zero-results triggers a relaxed-fallback ladder.
 * ───────────────────────────────────────────────────────────────────── */

const CITY_OPTIONS = [
  { k: "", l: "All cities" },
  { k: "London", l: "London" },
  { k: "Mumbai", l: "Mumbai" },
  { k: "Dubai", l: "Dubai" },
  { k: "New York", l: "New York" },
  { k: "Paris", l: "Paris" },
  { k: "Berlin", l: "Berlin" },
  { k: "Singapore", l: "Singapore" },
  { k: "Sydney", l: "Sydney" },
];

const WHEN_OPTIONS_REFINE = [
  { k: "", l: "Anytime" },
  { k: "today", l: "Today" },
  { k: "weekend", l: "This weekend" },
  { k: "this-week", l: "This week" },
  { k: "next-week", l: "Next week" },
  { k: "this-month", l: "This month" },
];

const PRICE_BANDS_REFINE = [
  { k: "", l: "Any price" },
  { k: "0-100", l: "Under 100" },
  { k: "100-300", l: "100 – 300" },
  { k: "300+", l: "300+" },
];

const ACCESS_ROUTE_OPTIONS = [
  { k: "concierge", l: "Concierge® can arrange" },
  { k: "onsite",    l: "Official venue route" },
  { k: "direct",    l: "Direct public booking" },
];

const CATEGORY_OPTIONS_REFINE = [
  { k: "all", l: "All categories" },
  { k: "music", l: "Music" },
  { k: "theatre", l: "Theatre" },
  { k: "sports", l: "Sports" },
  { k: "festivals", l: "Festivals" },
  { k: "comedy", l: "Comedy" },
  { k: "family", l: "Family" },
  { k: "arts", l: "Arts" },
];

function _filterCount(pf, committed) {
  // How many filters are set vs default. Used in "Apply filters (N)".
  let n = 0;
  if ((pf.tab || "all") !== "all") n++;
  if (pf.city) n++;
  if (pf.when) n++;
  if (pf.priceBand) n++;
  if (pf.bookingPaths && pf.bookingPaths.size > 0) n++;
  return n;
}

function RefineRow({ label, value, options, fieldKey, pending, setPending }) {
  const [expanded, setExpanded] = React.useState(false);
  const valueLabel = (() => {
    if (fieldKey === "bookingPaths") {
      if (!value || value.size === 0) return "All routes";
      return Array.from(value).map((k) => {
        const m = ACCESS_ROUTE_OPTIONS.find((o) => o.k === k);
        return m ? m.l : k;
      }).join(", ");
    }
    const m = (options || []).find((o) => o.k === (value || ""));
    return m ? m.l : (value || "—");
  })();

  return (
    <div
      data-testid={`refine-row-${fieldKey}`}
      style={{
        borderBottom: "1px solid var(--t-line)",
        padding: "16px 0",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: 0,
          background: "none", border: "none", cursor: "pointer",
        }}
      >
        <span style={{ color: "var(--t-ink)", fontSize: 15, fontWeight: 500 }}>{label}</span>
        <span style={{
          color: "var(--t-ink3)", fontSize: 14, display: "flex",
          alignItems: "center", gap: 8,
        }}>
          {valueLabel}
          <span style={{ fontSize: 18 }}>{expanded ? "−" : "+"}</span>
        </span>
      </button>
      {expanded && (
        <div style={{ paddingTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {fieldKey === "bookingPaths" ? (
            ACCESS_ROUTE_OPTIONS.map((o) => {
              const set = pending.bookingPaths || new Set();
              const active = set.has(o.k);
              return (
                <button
                  key={o.k}
                  onClick={() => {
                    const next = new Set(set);
                    if (next.has(o.k)) next.delete(o.k); else next.add(o.k);
                    setPending({ ...pending, bookingPaths: next });
                  }}
                  data-testid={`refine-access-${o.k}`}
                  style={{
                    padding: "8px 14px", borderRadius: 100,
                    fontSize: 13, cursor: "pointer",
                    background: active ? "var(--t-ink)" : "transparent",
                    color: active ? "var(--t-paper)" : "var(--t-ink)",
                    border: `1px solid ${active ? "var(--t-ink)" : "var(--t-line)"}`,
                  }}
                >{o.l}</button>
              );
            })
          ) : (
            options.map((o) => {
              const active = (value || "") === o.k || (fieldKey === "tab" && !value && o.k === "all");
              return (
                <button
                  key={o.k || "any"}
                  onClick={() => setPending({ ...pending, [fieldKey]: o.k })}
                  data-testid={`refine-${fieldKey}-${o.k || "any"}`}
                  style={{
                    padding: "8px 14px", borderRadius: 100,
                    fontSize: 13, cursor: "pointer",
                    background: active ? "var(--t-ink)" : "transparent",
                    color: active ? "var(--t-paper)" : "var(--t-ink)",
                    border: `1px solid ${active ? "var(--t-ink)" : "var(--t-line)"}`,
                  }}
                >{o.l}</button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function RefineDrawer({
  open, pending, setPending, onApply, onClearAll, onCancel,
}) {
  if (!open) return null;
  const filterCount = _filterCount(pending);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        data-testid="refine-backdrop"
        style={{
          position: "fixed", inset: 0, background: "rgba(20,16,12,0.55)",
          zIndex: 90,
        }}
      />
      {/* Drawer · side on desktop, bottom-sheet on mobile via @media */}
      <div
        data-testid="refine-drawer"
        style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          width: "min(440px, 100vw)",
          background: "var(--t-paper)",
          zIndex: 100,
          boxShadow: "-12px 0 40px rgba(20,16,12,0.18)",
          display: "flex", flexDirection: "column",
        }}
        className="enjoy-refine-drawer"
      >
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid var(--t-line)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h3 className="t-serif" style={{ fontSize: 22, margin: 0, color: "var(--t-ink)" }}>
            Refine your search
          </h3>
          <button
            onClick={onCancel}
            data-testid="refine-close"
            aria-label="Close refine drawer"
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 24, color: "var(--t-ink3)", lineHeight: 1,
            }}
          >×</button>
        </div>

        <div style={{
          flex: 1, overflowY: "auto", padding: "8px 24px",
        }}>
          <RefineRow label="Category"      value={pending.tab}      options={CATEGORY_OPTIONS_REFINE} fieldKey="tab"          pending={pending} setPending={setPending} />
          <RefineRow label="City"          value={pending.city}     options={CITY_OPTIONS}            fieldKey="city"         pending={pending} setPending={setPending} />
          <RefineRow label="Date"          value={pending.when}     options={WHEN_OPTIONS_REFINE}     fieldKey="when"         pending={pending} setPending={setPending} />
          <RefineRow label="Price"         value={pending.priceBand} options={PRICE_BANDS_REFINE}     fieldKey="priceBand"    pending={pending} setPending={setPending} />
          <RefineRow label="Access route"  value={pending.bookingPaths} options={ACCESS_ROUTE_OPTIONS} fieldKey="bookingPaths" pending={pending} setPending={setPending} />
        </div>

        <div style={{
          padding: "16px 24px", borderTop: "1px solid var(--t-line)",
          background: "var(--t-paper2)",
        }}>
          <button
            type="button"
            onClick={onApply}
            data-testid="refine-apply"
            className="enjoy-btn"
            style={{ width: "100%", padding: "14px 0", fontSize: 14, fontWeight: 600 }}
          >
            Apply filters{filterCount > 0 ? ` (${filterCount})` : ""}
          </button>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
            <button
              type="button"
              onClick={onClearAll}
              data-testid="refine-clear"
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 13, color: "var(--t-ink2)", padding: "8px 4px",
              }}
            >Clear all</button>
            <button
              type="button"
              onClick={onCancel}
              data-testid="refine-cancel"
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 13, color: "var(--t-ink2)", padding: "8px 4px",
              }}
            >Cancel</button>
          </div>
        </div>
      </div>
    </>
  );
}

function ActiveFiltersBar({
  tab, city, when, priceBand, bookingPaths,
  onClearTab, onClearCity, onClearWhen, onClearPrice, onClearBookingPath, onClearAll,
}) {
  // Doctrine · defaults are invisible. "All" / "Any" / "Anytime" /
  // "Any price" never render as chips. Only show what the member set.
  const chips = [];
  if (tab && tab !== "all") {
    const m = CATEGORY_OPTIONS_REFINE.find((o) => o.k === tab);
    chips.push({ id: "tab",  l: m ? m.l : tab,  on: onClearTab });
  }
  if (city)        chips.push({ id: "city",  l: city, on: onClearCity });
  if (when) {
    const m = WHEN_OPTIONS_REFINE.find((o) => o.k === when);
    chips.push({ id: "when",  l: m ? m.l : when, on: onClearWhen });
  }
  if (priceBand) {
    const m = PRICE_BANDS_REFINE.find((o) => o.k === priceBand);
    chips.push({ id: "price", l: m ? m.l : priceBand, on: onClearPrice });
  }
  if (bookingPaths && bookingPaths.size > 0) {
    Array.from(bookingPaths).forEach((bp) => {
      const m = ACCESS_ROUTE_OPTIONS.find((o) => o.k === bp);
      chips.push({ id: `path-${bp}`, l: m ? m.l : bp, on: () => onClearBookingPath(bp) });
    });
  }
  if (chips.length === 0) return null;
  return (
    <div
      data-testid="enjoy-active-filters"
      style={{
        display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
        padding: "12px 0",
      }}
    >
      {chips.map((c) => (
        <span
          key={c.id}
          data-testid={`enjoy-active-chip-${c.id}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", borderRadius: 100,
            background: "var(--t-ink)", color: "var(--t-paper)",
            fontSize: 12.5, fontWeight: 500,
          }}
        >
          {c.l}
          <button
            onClick={c.on}
            aria-label={`Remove ${c.l} filter`}
            data-testid={`enjoy-active-chip-${c.id}-remove`}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--t-paper)", padding: 0, marginLeft: 2,
              fontSize: 14, lineHeight: 1, opacity: 0.7,
            }}
          >×</button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        data-testid="enjoy-clear-all-filters"
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 12.5, color: "var(--t-ink2)",
          textDecoration: "underline", textUnderlineOffset: 3,
          padding: "6px 8px",
        }}
      >Clear all filters</button>
    </div>
  );
}

function PlacesRail({ places, onConcierge }) {
  // Place/attraction answers · 18 Jul 2026. "/enjoy is an events engine,
  // but an amusement park is a place" — when the query carries a place
  // intent the backend ships Google Places cards; they render above the
  // grid with a Maps link + a Concierge® route.
  if (!places || places.length === 0) return null;
  const label = places[0].category_label || "Places";
  return (
    <section data-testid="enjoy-places-rail" style={{ padding: "20px 0 8px" }}>
      <h3
        className="t-mono uppercase"
        style={{ fontSize: 11, letterSpacing: "0.22em", color: "var(--t-ink2)", margin: "0 0 14px" }}
      >
        {label} · worth the visit →
      </h3>
      <div style={{
        display: "grid", gap: 14,
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      }}>
        {places.map((p) => (
          <div
            key={p.place_id}
            data-testid={`enjoy-place-card-${p.place_id}`}
            style={{
              border: "1px solid var(--t-line)", borderRadius: 14,
              background: "var(--t-paper)", overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}
          >
            <div style={{
              height: 140,
              background: p.photo_url
                ? `url(${p.photo_url}) center/cover`
                : "var(--t-brass-wash, rgba(160,122,56,0.12))",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {!p.photo_url && (
                <span className="t-serif" style={{ color: "var(--t-brass)", fontSize: 34 }}>
                  {(p.name || "?").charAt(0)}
                </span>
              )}
            </div>
            <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--t-ink)" }}>{p.name}</div>
              <div className="t-mono" style={{ fontSize: 10.5, color: "var(--t-ink3)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {p.rating != null && <span>★ {p.rating} ({(p.ratings_total || 0).toLocaleString()})</span>}
                {p.open_now === true && <span style={{ color: "#2e7d32" }}>● Open now</span>}
                {p.open_now === false && <span style={{ color: "var(--t-ink3)" }}>○ Closed now</span>}
              </div>
              {p.address && (
                <div style={{ fontSize: 12, color: "var(--t-ink3)", lineHeight: 1.4 }}>{p.address}</div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: "auto", paddingTop: 8 }}>
                <button
                  type="button"
                  data-testid={`enjoy-place-concierge-${p.place_id}`}
                  onClick={() => onConcierge && onConcierge(p)}
                  className="enjoy-btn"
                  style={{ fontSize: 12, padding: "8px 14px" }}
                >Concierge® route</button>
                {p.maps_url && (
                  <a
                    href={p.maps_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`enjoy-place-map-${p.place_id}`}
                    className="enjoy-btn ghost"
                    style={{ fontSize: 12, padding: "8px 14px", textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                  >Map ↗</a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


function WhatsOnTonight({ onOpen }) {
  // "What's on tonight" · landing-only strip powered by the merged
  // events pipe (when=today). Honest silence: fewer than 3 real rows →
  // the rail renders nothing.
  const CITIES = [
    { c: "Mumbai", cc: "IN" }, { c: "London", cc: "GB" },
    { c: "Dubai", cc: "AE" }, { c: "New York", cc: "US" },
  ];
  const [railCity, setRailCity] = React.useState("Mumbai");
  const [events, setEvents] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    setEvents(null);
    const cc = (CITIES.find((x) => x.c === railCity) || {}).cc;
    enjoy.search({ tab: "all", city: railCity, country: cc, when: "today", page: 1, pageSize: 8 })
      .then((r) => { if (!cancelled) setEvents((r && r.events) || []); })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, [railCity]);

  const rows = (events || []).slice(0, 8);
  return (
    <section data-testid="enjoy-tonight-rail" style={{ padding: "8px 0 28px" }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 14, flexWrap: "wrap", gap: 10,
      }}>
        <h3
          className="t-mono uppercase"
          style={{ fontSize: 11, letterSpacing: "0.22em", color: "var(--t-ink2)", margin: 0 }}
        >
          What's on tonight →
        </h3>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {CITIES.map(({ c }) => (
            <button
              key={c}
              type="button"
              onClick={() => setRailCity(c)}
              data-testid={`enjoy-tonight-city-${c.replace(/\s/g, "-").toLowerCase()}`}
              className="t-mono uppercase"
              style={{
                fontSize: 9.5, letterSpacing: "0.12em", fontWeight: 600,
                padding: "5px 12px", borderRadius: 100, cursor: "pointer",
                background: railCity === c ? "var(--t-brass)" : "transparent",
                color: railCity === c ? "var(--t-paper)" : "var(--t-ink2)",
                border: `1px solid ${railCity === c ? "var(--t-brass)" : "var(--t-line2)"}`,
                transition: "background-color 0.15s, color 0.15s",
              }}
            >{c}</button>
          ))}
        </div>
      </div>
      {events === null ? (
        <div className="t-mono" style={{ fontSize: 11, color: "var(--t-ink3)", letterSpacing: "0.1em" }}
          data-testid="enjoy-tonight-loading">CHECKING THE CALENDAR…</div>
      ) : rows.length < 3 ? (
        <div className="t-mono" style={{ fontSize: 11, color: "var(--t-ink3)", letterSpacing: "0.1em" }}
          data-testid="enjoy-tonight-empty">A quiet night in {railCity} — ask the Concierge® what the house recommends.</div>
      ) : (
        <div style={{
          display: "grid", gap: 12,
          gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
        }}>
          {rows.map((ev) => {
            const img = ev.hero_image_url || ev.image_url;
            return (
              <button
                key={ev.event_id}
                type="button"
                onClick={() => onOpen && onOpen(ev)}
                data-testid={`enjoy-tonight-card-${ev.event_id}`}
                style={{
                  textAlign: "left", padding: 12,
                  border: "1px solid var(--t-line)", borderRadius: 12,
                  background: "var(--t-paper)", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 12,
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--t-brass)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--t-line)"; }}
              >
                <div style={{
                  width: 52, height: 52, borderRadius: 8, flexShrink: 0,
                  border: "1px solid var(--t-line)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: img ? `url(${img}) center/cover` : "var(--t-brass-wash, rgba(160,122,56,0.12))",
                }}>
                  {!img && (
                    <span className="t-serif" style={{ color: "var(--t-brass)", fontSize: 20 }}>
                      {(ev.title || "?").charAt(0)}
                    </span>
                  )}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: 13.5, fontWeight: 600, color: "var(--t-ink)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{ev.title}</div>
                  <div className="t-mono" style={{
                    fontSize: 10, color: "var(--t-ink3)", marginTop: 3,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {[ev.venue_name, ev.date_note || "Tonight"].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}


function PopularRightNow({ onPick }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    enjoy.popular(8).then((res) => {
      if (!cancelled) setData(res);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Founder doctrine · if fewer than 3 verified rows exist, hide the
  // carousel entirely rather than padding with mock data.
  if (!data || data.hide || (data.events || []).length < 3) return null;
  return (
    <section
      data-testid="enjoy-popular-right-now"
      style={{ padding: "8px 0 32px" }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 16,
      }}>
        <h3
          className="t-mono uppercase"
          style={{ fontSize: 11, letterSpacing: "0.22em", color: "var(--t-ink2)", margin: 0 }}
        >
          Popular right now →
        </h3>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 14,
      }}>
        {data.events.slice(0, 4).map((ev) => (
          <button
            key={ev.id}
            onClick={() => onPick && onPick(ev)}
            data-testid={`enjoy-popular-card-${ev.id}`}
            style={{
              textAlign: "left", padding: 14,
              border: "1px solid var(--t-line)", borderRadius: 12,
              background: "var(--t-paper)", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 12,
              transition: "border-color 0.15s, transform 0.1s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--t-brass)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--t-line)"; }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: 8,
              /* Founder ruling 16 Feb 2026 PM-17: read the same image
                 hierarchy as the main cards · prefer `hero_image_url`
                 (the wire-safe proxy URL for Places + designed SVG
                 placeholders) and fall back to `image_url` for the
                 legacy desk-curated channel. */
              background: (ev.hero_image_url || ev.image_url)
                ? `url(${ev.hero_image_url || ev.image_url}) center/cover`
                : "var(--t-paper2)",
              flexShrink: 0, border: "1px solid var(--t-line)",
            }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                color: "var(--t-ink)", fontSize: 14, fontWeight: 600,
                marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden",
                textOverflow: "ellipsis",
              }}>{ev.title}</div>
              <div style={{ color: "var(--t-ink3)", fontSize: 12 }}>
                {[ev.category, ev.city].filter(Boolean).join(" · ")}
              </div>
              {ev.date_note && (
                <div style={{ color: "var(--t-ink3)", fontSize: 11, marginTop: 2 }}>
                  {ev.date_note}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ── <SignatureHero/> · founder ruling 16 Feb 2026 PM-11 (Workstream A)
//
// Renders the image hierarchy for a Signature Experience card.
// Receives the wire-shape doc · `hero_image_source_type` is the source
// of truth · "desk_curated" / "google_places" / "none". On the
// google_places path, the credit (`html_attributions[0]`) is rendered
// directly under the image · the founder ruling REQUIRES the credit
// always shows when the photo came from Places.
//
// Honest fallback: when `hero_image_url` is null OR when the image
// fails to load, the card renders a brass-washed monogram block
// (using the title's first letter) instead of a broken-image glyph.
function SignatureHero({ event }) {
  const [failed, setFailed] = React.useState(false);
  const src = event && event.hero_image_url;
  const sourceType = event && event.hero_image_source_type;
  const credits = (event && event.google_photo_html_attributions) || [];
  const showAttribution = sourceType === "google_places" && credits.length > 0;

  // Founder ruling 16 Feb 2026 PM-14: NEVER render a letter monogram
  // on a member-facing card. If there's no URL, render a blank tinted
  // placeholder (the publish gate at the backend should have prevented
  // this card from reaching the member — this is purely defensive).
  if (!src || failed) {
    return (
      <div
        data-testid={`enjoy-signature-hero-fallback-${event && event.id}`}
        style={{
          width: "100%", aspectRatio: "16/10",
          background: "var(--t-brass-wash, rgba(160, 122, 56, 0.08))",
          borderBottom: "1px solid var(--t-line)",
        }}
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      data-testid={`enjoy-signature-hero-${event.id}`}
      style={{ position: "relative", width: "100%" }}
    >
      <CldImg
        src={src}
        w={1200}
        eager
        alt=""
        onError={() => setFailed(true)}
        style={{
          width: "100%", aspectRatio: "16/10", objectFit: "cover",
          display: "block",
          borderBottom: "1px solid var(--t-line)",
        }}
      />
      {showAttribution && (
        <div
          data-testid={`enjoy-signature-hero-attribution-${event.id}`}
          className="t-mono"
          style={{
            position: "absolute", left: 8, bottom: 8,
            fontSize: 9.5, letterSpacing: "0.04em",
            color: "#fff",
            background: "rgba(0, 0, 0, 0.55)",
            padding: "3px 8px", borderRadius: 100,
            backdropFilter: "blur(4px)",
            maxWidth: "calc(100% - 16px)", overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
          // Render the HTML attribution as-is · per Google Places ToS the
          // anchor MUST stay clickable. We use dangerouslySetInnerHTML
          // because the html_attributions value is a `<a href="...">Name</a>`
          // string straight from the Places API · it's safe to render
          // because Google constructs it server-side.
          dangerouslySetInnerHTML={{ __html: `Photo · ${credits[0]}` }}
        />
      )}
    </div>
  );
}


function SignatureCarousel({ onPick, onExplore }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    // Founder ruling 16 Feb 2026 PM-20 · landing shelf shows MAX 3
    // Signature cards. The full 149-row catalogue lives at /enjoy/signature.
    enjoy.signaturePublished(3).then((res) => {
      if (!cancelled) setData(res);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const items = (data && data.items) || [];
  if (items.length < 3) return null;  // doctrine · hide rather than fill
  return (
    <section
      data-testid="enjoy-signature-carousel"
      style={{ padding: "8px 0 40px" }}
    >
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        marginBottom: 16,
      }}>
        <div>
          <h3
            className="t-serif"
            style={{ fontSize: 22, margin: 0, color: "var(--t-ink)" }}
          >
            Signature Experiences
          </h3>
          <div className="t-mono uppercase" style={{
            fontSize: 10, letterSpacing: "0.22em", color: "var(--t-brass)",
            marginTop: 4,
          }}>
            Curated by the Concierge® · by arrangement
          </div>
        </div>
        <button
          type="button"
          onClick={onExplore}
          data-testid="enjoy-explore-signature"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--t-brass)", fontSize: 13,
            textDecoration: "underline", textUnderlineOffset: 3,
          }}
        >Explore Signature Experiences →</button>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
      }}>
        {items.slice(0, 3).map((ev) => (
          <button
            key={ev.id}
            onClick={() => onPick && onPick(ev)}
            data-testid={`enjoy-signature-card-${ev.id}`}
            style={{
              textAlign: "left", padding: 0,
              border: "1px solid var(--t-line)", borderRadius: 14,
              background: "var(--t-paper)", cursor: "pointer",
              display: "flex", flexDirection: "column",
              transition: "all 0.15s",
              minHeight: 240, overflow: "hidden",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--t-brass)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--t-line)";
              e.currentTarget.style.transform = "none";
            }}
          >
            {/* Hero image · founder ruling 16 Feb 2026 PM-11 ·
                Workstream A. Three hierarchy paths render here:
                  · desk_curated → use the URL as-is, no credit
                  · google_places → use the proxy URL, credit below
                  · none → render no image (honest silence) */}
            <SignatureHero event={ev} />
            <div style={{
              padding: "16px 22px 20px",
              display: "flex", flexDirection: "column", gap: 8, flex: 1,
            }}>
              <div className="t-mono uppercase" style={{
                fontSize: 10, letterSpacing: "0.18em",
                color: "var(--t-brass)",
              }}>
                {ev.member_badge || "Curated by Concierge®"}
              </div>
              <div className="t-serif" style={{
                color: "var(--t-ink)", fontSize: 18, lineHeight: 1.25,
                marginTop: 2,
              }}>{ev.title}</div>
              <div style={{ color: "var(--t-ink2)", fontSize: 13 }}>
                {[ev.category, ev.city].filter(Boolean).join(" · ")}
              </div>
              {ev.concierge_whisper && (
                <div style={{
                  color: "var(--t-ink3)", fontSize: 12.5, lineHeight: 1.45,
                  fontStyle: "italic", marginTop: "auto",
                }}>
                  &ldquo;{ev.concierge_whisper.slice(0, 130)}{ev.concierge_whisper.length > 130 ? "…" : ""}&rdquo;
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}


/* ── Main page ────────────────────────────────────────────────────────── */
export default function Enjoy() {
  const navigate = useNavigate();
  const [member, setMember] = useState(() => loadStoredMember());
  const [tab, setTab] = useState("all");
  const [city, setCity] = useState("");
  const [when, setWhen] = useState("");
  const [priceBand, setPriceBand] = useState("");
  // Booking path filter · mirrors /table's ⚡ Instant · ☎ Call · ✉ Contact · ✦ Concierge®
  // Set of selected paths. Empty = all paths shown.
  const [bookingPaths, setBookingPaths] = useState(new Set());
  // /table-parity chip strip · founder ruling 15 Feb 2026 PM-5. Primary
  // categories always visible; long-tail behind a "More ⌄" toggle.
  const [moreChipsOpen, setMoreChipsOpen] = useState(false);

  // ── Search-first redesign · founder ruling 16 Feb 2026 PM-8 ──────────
  // The first screen carries only Search + Find it + Ask Concierge® +
  // Refine search. All filter chips (category, city, date, price, booking
  // path) move BEHIND a single Refine drawer with Apply/Clear/Cancel.
  // Defaults are invisible; active filters only show as removable chips
  // AFTER the member taps Apply.
  const [refineOpen, setRefineOpen] = useState(false);
  // Staging copy of the filters inside the drawer · committed only on Apply
  const [pendingFilters, setPendingFilters] = useState({
    tab: "all", city: "", when: "", priceBand: "",
    bookingPaths: new Set(),
  });
  // Whether the member has actively SET any filter (used to hide "All/Any"
  // defaults from the active-chip strip).
  const filtersAreActive = (
    (tab && tab !== "all") || city || when || priceBand ||
    (bookingPaths && bookingPaths.size > 0)
  );
  function openRefine() {
    // Snapshot the current committed state into the staging copy
    setPendingFilters({
      tab, city, when, priceBand,
      bookingPaths: new Set(bookingPaths),
    });
    setRefineOpen(true);
  }
  function applyRefine() {
    setTab(pendingFilters.tab || "all");
    setCity(pendingFilters.city || "");
    setWhen(pendingFilters.when || "");
    setPriceBand(pendingFilters.priceBand || "");
    setBookingPaths(new Set(pendingFilters.bookingPaths || []));
    setPage(1);
    setRefineOpen(false);
  }
  function clearAllRefine() {
    setPendingFilters({
      tab: "all", city: "", when: "", priceBand: "",
      bookingPaths: new Set(),
    });
  }
  function clearAllCommitted() {
    setTab("all"); setCity(""); setWhen(""); setPriceBand("");
    setBookingPaths(new Set()); setPage(1);
  }

  // Relaxed-fallback results · founder doctrine: when the committed
  // filter set returns 0, the desk relaxes filters in order
  // (price → booking → date → category → city) and surfaces the closest
  // real matches. We never invent generic overlays.
  const [relaxedResults, setRelaxedResults] = useState(null);
  const [relaxedReason, setRelaxedReason] = useState(null);
  const [query, setQuery] = useState("");
  // Whether a search query is active. Founder ruling 16 Feb 2026 PM-19:
  // when a query is typed, the page is in "results mode" — generic
  // Signature / Popular carousels MUST hide. They are landing-page
  // discovery content, not a replacement for search.
  const hasActiveQuery = Boolean(query && query.trim().length > 0);
  const isResultsMode = hasActiveQuery || filtersAreActive;
  const [page, setPage] = useState(1);
  const [results, setResults] = useState({ events: [], total: 0, page: 1, facets: {}, find_rails: [], find_rails_resolution: null, category_doors: [], category_doors_resolution: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [requestingEvent, setRequestingEvent] = useState(null);
  const [successRef, setSuccessRef] = useState(null);

  // FIR-20260217-SEARCH · Layer 1 truth-in-UI · founder ruling 17 Feb 2026 PM.
  // While the member is still typing, the grid backend may return 0 for the
  // half-typed query ("mousetr" → 0 via TM /events) even though the final
  // intended query ("mousetrap") will return results. Never show the
  // "We couldn't find an exact match" empty state during a typing window —
  // render the loading state instead so the page never lies "couldn't find"
  // mid-keystroke. The empty-state condition uses (!loading && !stillTyping).
  const [stillTyping, setStillTyping] = useState(false);
  const _typingTimerRef = useRef(null);
  useEffect(() => {
    if (_typingTimerRef.current) clearTimeout(_typingTimerRef.current);
    setStillTyping(true);
    _typingTimerRef.current = setTimeout(() => setStillTyping(false), 750);
    return () => { if (_typingTimerRef.current) clearTimeout(_typingTimerRef.current); };
  }, [query]);

  // Dynamic per-city category strip · founder ruling 14 Feb 2026 PM.
  // Categories come from what the city's platforms (BMS/District/
  // Platinumlist/TM/DICE/Skiddle) actually serve — not the gated 7.
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  // Append the always-on Signature Experiences chip at the tail · the
  // backend doesn't return it (kind=experience is its own switch).
  const tabsWithExperience = useMemo(() => {
    const sig = { key: "_experience", label: "Signature Experiences",
                  icon: "Star", platforms: [] };
    // Don't duplicate if backend already includes it.
    if (categories.some((c) => c.key === "_experience")) return categories;
    return [...categories, sig];
  }, [categories]);

  useEffect(() => {
    let cancelled = false;
    enjoy.categories({ city: city || undefined })
      .then((r) => {
        if (cancelled || !r || !Array.isArray(r.categories)) return;
        setCategories(r.categories);
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [city]);

  const isExperience = tab === "_experience";
  const activeCategoryTab = isExperience ? "all" : tab;

  const _searchSeqRef = useRef(0);
  const loadResults = useCallback(async () => {
    // Out-of-order guard · 18 Jul 2026. Concurrent searches (fast landing
    // fetch vs slower query fetch) could resolve out of order and let a
    // STALE response overwrite the member's actual results. Only the
    // latest-issued request may touch state.
    const seq = ++_searchSeqRef.current;
    setLoading(true); setError(null);
    setRelaxedResults(null); setRelaxedReason(null);
    try {
      const r = await enjoy.search({
        tab: activeCategoryTab,
        city: city || undefined,
        when: when || undefined,
        priceBand: priceBand || undefined,
        q: query || undefined,
        kind: isExperience ? "experience" : undefined,
        page, pageSize: 24,
      });
      if (seq !== _searchSeqRef.current) return;
      setResults(r);

      // ── Relaxed-fallback ladder · founder ruling 16 Feb 2026 PM-8 ──
      // When the committed filter set returns zero AND the member has
      // active filters, the desk relaxes filters in doctrine order
      // (price → booking → date → category → city) and shows the closest
      // real matches under "Closest matches we can route". We never
      // invent generic overlays · only real verified rows.
      const isZero = !(r && r.events && r.events.length > 0);
      const anyFilter = (
        (activeCategoryTab && activeCategoryTab !== "all") ||
        city || when || priceBand || (bookingPaths && bookingPaths.size > 0)
      );
      if (isZero && anyFilter) {
        const ladder = [
          { drop: ["priceBand"],   reason: "price" },
          { drop: ["priceBand", "bookingPaths"], reason: "booking route" },
          { drop: ["priceBand", "bookingPaths", "when"], reason: "date" },
          { drop: ["priceBand", "bookingPaths", "when", "tab"], reason: "category" },
          { drop: ["priceBand", "bookingPaths", "when", "tab", "city"], reason: "city" },
        ];
        for (const step of ladder) {
          const params = {
            tab: step.drop.includes("tab") ? "all" : activeCategoryTab,
            city: step.drop.includes("city") ? undefined : (city || undefined),
            when: step.drop.includes("when") ? undefined : (when || undefined),
            priceBand: step.drop.includes("priceBand") ? undefined : (priceBand || undefined),
            q: query || undefined,
            kind: isExperience ? "experience" : undefined,
            page: 1, pageSize: 12,
          };
          try {
            const rr = await enjoy.search(params);
            if (seq !== _searchSeqRef.current) return;
            if (rr && rr.events && rr.events.length > 0) {
              setRelaxedResults(rr);
              setRelaxedReason(step.reason);
              break;
            }
          } catch (_e) {
            // Keep trying the next rung — one failed leg shouldn't kill the ladder.
          }
        }
      }
    } catch (e) {
      if (seq !== _searchSeqRef.current) return;
      setError(e.message || "Could not load events.");
      setResults({ events: [], total: 0, page: 1, facets: {}, find_rails: [], find_rails_resolution: null, category_doors: [], category_doors_resolution: null });
    } finally {
      if (seq === _searchSeqRef.current) setLoading(false);
    }
  }, [activeCategoryTab, city, when, priceBand, bookingPaths, query, page, isExperience]);

  useEffect(() => { loadResults(); }, [loadResults]);

  // ── Deep-link auto-open · founder ruling 18 Feb 2026 ───────────────────
  // /enjoy/signature cards navigate here with `?signature=<id>` (and our
  // own share-action emits `?event=<id>&src=<source>`). Read those once on
  // mount, fetch the row's full detail, populate `activeEvent` so the
  // EventDrawer auto-opens. We then strip the param from the URL so
  // back/forward navigation behaves normally and a refresh doesn't
  // re-pop the drawer.
  const didDeepLinkRef = useRef(false);
  useEffect(() => {
    if (didDeepLinkRef.current) return;
    didDeepLinkRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const sigId = params.get("signature");
    const evId = params.get("event");
    const evSrc = params.get("src");
    let source = null, eventId = null;
    if (sigId) { source = "curated"; eventId = sigId; }
    else if (evId && evSrc) { source = evSrc; eventId = evId; }
    if (!source || !eventId) return;
    enjoy.detail(source, eventId)
      .then((full) => {
        if (full && full.event_id) setActiveEvent(full);
      })
      .catch(() => { /* row removed or expired · honest silence */ })
      .finally(() => {
        // Strip the deep-link param so refresh / nav back doesn't re-open.
        const url = new URL(window.location.href);
        url.searchParams.delete("signature");
        url.searchParams.delete("event");
        url.searchParams.delete("src");
        window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams.toString()}` : "") + url.hash);
      });
  }, []);

  function goHome() {
    setTab("all"); setCity(""); setWhen(""); setPriceBand("");
    setQuery(""); setPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleRequestConcierge(event) {
    // Founder ruling 14 Feb 2026 PM: "no gate for members to this · like
    // /table." /enjoy and /table are open to all — only /club has membership.
    // Anyone can request via Concierge®; member status is metadata, not a gate.
    enjoyFireClick(event, "concierge_request");
    setRequestingEvent(event);
  }

  function handleBookDirect(event, route) {
    // FIR-20260217-TMSG · also fire the enjoy_telemetry beacon. This is
    // distinct from enjoy.logIntent (which is the deeper provenance log
    // for /api/click-intent). The new beacon goes to lc_enjoy_telemetry
    // for fast bucket/cta analysis · cta_kind resolves to
    // "box_office_tier1" when the URL was rewritten by Tier 1.
    enjoyFireClick(event, "box_office");
    // Founder ruling 16 Feb 2026 PM-22 · universal click-intent.
    // The <a target="_blank"> opens the deep link natively (user-gesture
    // preserved · popup-blocker safe). This fires in parallel · the
    // network call is `keepalive: true` so it survives the page nav.
    // We send a SANITISED snapshot only — the backend enforces the
    // whitelist again per TELEMETRY_DOCTRINE.md §5.
    const safeSnapshot = {
      title: event.title,
      source: event.source,
      rail: (route && route.kind) || event.source,
      entity_id: event.event_id,
      city: event.city,
      country: event.country,
      date: event.date_local || event.date,
      time: event.time_local || event.time,
      price_shown: event.price_min_display || event.price_text,
      currency: event.currency,
      category: event.category,
      kind: event.kind,
      surface: "enjoy",
      source_url: event.url || event.direct_book_url,
      destination_url: route && route.url,
      provider_name: route && route.kind,
      image_url: event.image_url || event.hero_image_url,
    };
    enjoy.logIntent({
      type: "direct_book_click",
      source: event.source,            // legacy field · preserved for compat
      event_id: event.event_id,        // legacy field · preserved for compat
      event_snapshot: safeSnapshot,    // backend re-sanitises before persist
      notes: route ? `route=${route.kind}|url=${route.url}` : undefined,
    });
  }

  function handleRequestSubmitted(resp) {
    setRequestingEvent(null);
    setActiveEvent(null);
    setSuccessRef(resp.display_ref || resp.intent_id);
    setTimeout(() => setSuccessRef(null), 8000);
  }

  function handleEmptyTellUs() {
    const generic = {
      source: "curated",
      event_id: "_generic",
      kind: "event",
      title: "Generic Concierge® request",
      category: activeCategoryTab,
      city: city || undefined,
      _is_generic: true,
    };
    handleRequestConcierge(generic);
  }

  // Quick-send (inline empty-state form) · founder ruling 16 Feb 2026.
  // Posts a minimal Concierge® request straight to /api/enjoy/request
  // without opening the full drawer. Carries the search context so the
  // desk has the brief on first read. /club member auto-fills contact;
  // guests pass name + (email or phone) inline.
  async function handleEmptyQuickSend({ note, name, email, phone }) {
    const payload = {
      source: "curated",
      event_id: "_generic",
      event_snapshot: {
        title: query
          ? `Search brief · "${query}"`
          : (city ? `Concierge brief · ${city}` : "Concierge® brief"),
        category: activeCategoryTab || "all",
        city: city || undefined,
        kind: "event",
      },
      preferences: [
        query ? `Searched: ${query}` : null,
        city ? `City chip: ${city}` : null,
        activeCategoryTab && activeCategoryTab !== "all" ? `Category: ${activeCategoryTab}` : null,
        when ? `When: ${when}` : null,
        (note || "").trim() ? `Note: ${(note || "").trim()}` : null,
      ].filter(Boolean).join(" · ") || "Member tapped Send to the desk from the empty state.",
      member_name: name || undefined,
      member_email: email || undefined,
      member_phone: phone || undefined,
    };
    const res = await enjoy.requestViaConcierge(payload, member?.card_no);
    enjoy.logIntent({
      type: "concierge_request",
      source: "curated",
      event_id: "_generic_empty_state",
      event_snapshot: payload.event_snapshot,
      notes: `empty_state_quick_send · query=${query || "—"} · city=${city || "—"}`,
    });
    return res;
  }

  return (
    <div data-table-surface data-testid="enjoy-page" className="t-grain lc-surface-with-float-pill">
      <TableHeader onHome={goHome} slug="enjoy" />
      <MemberContextBand pillar="enjoy" />

      <main className="max-w-6xl mx-auto px-6 pt-8 pb-28">
        {/* ── Hero · founder ruling 15 Feb 2026 PM-5 · mirror /table.
              Headline → one-line subtitle → BIG search bar (centred,
              with "Find it" CTA). Categories demoted to browse chips
              underneath, filters tucked quieter below that. */}
        <section style={{ paddingTop: 28, paddingBottom: 24, textAlign: "center" }}>
          <p
            className="t-mono uppercase"
            style={{ fontSize: 11, letterSpacing: "0.22em", color: "var(--t-brass)" }}
          >Concierge® · Enjoy</p>
          <h1
            className="t-serif"
            style={{
              fontSize: "clamp(40px, 5.6vw, 64px)",
              lineHeight: 1.02, letterSpacing: "-0.015em",
              color: "var(--t-ink)", fontWeight: 500,
              margin: "14px 0 18px",
            }}
            data-testid="enjoy-hero-title"
          >
            Name the night.{" "}
            <span style={{ color: "var(--t-brass)", fontStyle: "italic" }}>
              We open the door.
            </span>
          </h1>
          <p
            style={{
              fontSize: 17, color: "var(--t-ink2)", lineHeight: 1.55,
              maxWidth: 640, margin: "0 auto 36px",
            }}
            data-testid="enjoy-hero-subtitle"
          >
            Tell us who, where or what you&rsquo;re looking for.
            The Concierge® will find the right door.
          </p>

          {/* The bar IS the page · founder spec, /table-parity. */}
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <SuggestSearchInput
              large
              value={query}
              onChange={(v) => { setQuery(v); setPage(1); }}
              placeholder="Who, where or what…"
              onSubmit={() => { /* form-submit no-op · search runs on debounced state */ }}
              onPick={(pick) => {
                if (pick && pick.kind === "curated" && pick.id) {
                  enjoy.detail("curated", pick.id)
                    .then((ev) => {
                      if (ev && ev.event_id) setActiveEvent(ev);
                      else { setQuery(pick.name || ""); setPage(1); }
                    })
                    .catch(() => { setQuery(pick.name || ""); setPage(1); });
                  return;
                }
                setQuery(pick.name || pick.title || "");
                setPage(1);
              }}
            />
            <div
              data-testid="enjoy-hero-listening"
              style={{
                marginTop: 12, fontSize: 12.5,
                color: "var(--t-ink3, #8a7d6a)",
                fontStyle: "italic",
              }}
            >the Concierge® is listening…</div>
          </div>
        </section>

        {/* ── Search-first hero · founder ruling 16 Feb 2026 PM-8 ──────
              Category chips + filter chips ALL moved behind "Refine search".
              First-screen carries only Search + Find it + Ask Concierge® +
              Refine search + the (verified, optional) Popular + Signature
              carousels. Defaults stay invisible until the member sets them. */}

        {/* Inline secondary CTA · Ask the Concierge® · sits under the search bar */}
        <section
          style={{ display: "flex", justifyContent: "center", paddingTop: 4, paddingBottom: 18 }}
          data-testid="enjoy-hero-secondary-cta"
        >
          <button
            type="button"
            onClick={handleEmptyTellUs}
            data-testid="enjoy-ask-concierge-secondary"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 22px", borderRadius: 100,
              background: "transparent", color: "var(--t-brass)",
              border: "1px solid var(--t-brass)",
              fontSize: 13.5, fontWeight: 500, cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--t-brass)";
              e.currentTarget.style.color = "var(--t-paper)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--t-brass)";
            }}
          >
            Ask the Concierge®
          </button>
        </section>

        {/* Refine search button · primary access to the filter drawer */}
        <section
          style={{ display: "flex", justifyContent: "center", paddingBottom: 12 }}
          data-testid="enjoy-refine-row"
        >
          <button
            type="button"
            onClick={openRefine}
            data-testid="enjoy-refine-open"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 22px", borderRadius: 100,
              background: "var(--t-paper)", color: "var(--t-ink)",
              border: "1px solid var(--t-line2)",
              fontSize: 13, fontWeight: 500, cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--t-brass)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--t-line2)"; }}
          >
            <span style={{ fontSize: 14 }}>⇅</span>
            Refine search
          </button>
        </section>

        {/* Date chips · Today / This weekend / Next week · quick-tap filters
            powered by the events pipe (htichips) + TM/curated date windows. */}
        <section
          style={{ display: "flex", justifyContent: "center", gap: 8, paddingBottom: 12, flexWrap: "wrap" }}
          data-testid="enjoy-when-chips"
        >
          {[
            { k: "today", l: "Today" },
            { k: "weekend", l: "This weekend" },
            { k: "next-week", l: "Next week" },
          ].map((c) => {
            const active = when === c.k;
            return (
              <button
                key={c.k}
                type="button"
                onClick={() => { setWhen(active ? "" : c.k); setPage(1); }}
                data-testid={`enjoy-when-chip-${c.k}`}
                className="t-mono uppercase"
                style={{
                  fontSize: 10, letterSpacing: "0.14em", fontWeight: 600,
                  padding: "7px 16px", borderRadius: 100, cursor: "pointer",
                  background: active ? "var(--t-brass)" : "var(--t-paper)",
                  color: active ? "var(--t-paper)" : "var(--t-ink2)",
                  border: `1px solid ${active ? "var(--t-brass)" : "var(--t-line2)"}`,
                  transition: "background-color 0.15s, color 0.15s, border-color 0.15s",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = "var(--t-brass)"; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = "var(--t-line2)"; }}
              >{c.l}</button>
            );
          })}
        </section>

        {/* Active filter chips · ONLY shown after Apply.
            Defaults ("All", "Any", "Anytime", "Any price") never render here. */}
        {filtersAreActive && (
          <section data-testid="enjoy-active-filters-section" style={{
            display: "flex", justifyContent: "center", paddingBottom: 12,
          }}>
            <ActiveFiltersBar
              tab={tab}
              city={city}
              when={when}
              priceBand={priceBand}
              bookingPaths={bookingPaths}
              onClearTab={() => { setTab("all"); setPage(1); }}
              onClearCity={() => { setCity(""); setPage(1); }}
              onClearWhen={() => { setWhen(""); setPage(1); }}
              onClearPrice={() => { setPriceBand(""); setPage(1); }}
              onClearBookingPath={(bp) => {
                const next = new Set(bookingPaths);
                next.delete(bp);
                setBookingPaths(next);
                setPage(1);
              }}
              onClearAll={clearAllCommitted}
            />
          </section>
        )}

        {/* What's on tonight · landing only · powered by the events pipe
            (when=today) with a 4-city switcher. */}
        {!isResultsMode && (
          <WhatsOnTonight
            onOpen={(ev) => {
              if (!ev || !ev.event_id) return;
              enjoy.detail(ev.source || "curated", ev.event_id)
                .then((full) => {
                  if (full && full.event_id) setActiveEvent(full);
                  else { setQuery(ev.title || ""); setPage(1); }
                })
                .catch(() => { setQuery(ev.title || ""); setPage(1); });
            }}
          />
        )}

        {/* Popular right now · landing page only (no query, no filters).
            Founder ruling 16 Feb 2026 PM-19: hide on active search. */}
        {!isResultsMode && (
          <PopularRightNow
            onPick={(ev) => {
              // Mirror what clicking a search-suggest pick does — open the
              // detail drawer if it's a curated row, else search by title.
              if (ev && ev.event_id) {
                enjoy.detail("curated", ev.event_id)
                  .then((full) => {
                    if (full && full.event_id) setActiveEvent(full);
                    else { setQuery(ev.title || ""); setPage(1); }
                  })
                  .catch(() => { setQuery(ev.title || ""); setPage(1); });
              }
            }}
          />
        )}

        {/* Signature Experiences · landing-page-only premium discovery row.
            Founder ruling 16 Feb 2026 PM-19: generic Signature rows must
            NOT dominate active search results. They surface only when
            there is no query or filter, OR contextually as
            "Concierge® suggestions" inside the results split below. */}
        {!isResultsMode && (
          <SignatureCarousel
            onPick={(ev) => {
              if (ev && ev.id) {
                enjoy.detail("curated", ev.id)
                  .then((full) => {
                    if (full && full.event_id) setActiveEvent(full);
                  })
                  .catch(() => {});
              }
            }}
            onExplore={() => navigate("/enjoy/signature")}
          />
        )}

        {successRef && (
          <div
            data-testid="enjoy-success-banner"
            style={{
              marginTop: 22, padding: "12px 18px",
              background: "var(--t-verified-wash)",
              border: "1px solid var(--t-verified)",
              borderRadius: 8, color: "var(--t-verified)",
              fontSize: 13.5,
            }}
          >
            Sent to Concierge® · Reference {successRef}. Our desk will reply with options.
          </div>
        )}

        {successRef && (
          <div
            data-testid="enjoy-success-banner"
            style={{
              marginTop: 22, padding: "12px 18px",
              background: "var(--t-verified-wash)",
              border: "1px solid var(--t-verified)",
              borderRadius: 8, color: "var(--t-verified)",
              fontSize: 13.5,
            }}
          >
            Sent to Concierge® · Reference {successRef}. Our desk will reply with options.
          </div>
        )}

        {error && (
          <div
            data-testid="enjoy-error"
            style={{
              marginTop: 22, padding: "12px 18px",
              background: "#fee9e9", color: "#8a1717",
              border: "1px solid #e3b8b8", borderRadius: 8, fontSize: 13.5,
            }}
          >{error}</div>
        )}

        {/* ── Smart-search conflict banner · founder ruling 15 Feb 2026
              "Did you mean {event} in {city}?" surfaces when the city
              chip and the inferred city disagree. Never silently AND
              them — let the member choose. */}
        {results.conflict_banner && results.conflict_banner.kind === "city_conflict" && (
          <div
            data-testid="enjoy-conflict-banner"
            data-trigger={results.conflict_banner.trigger}
            style={{
              marginTop: 22, padding: "14px 18px",
              background: "var(--t-paper2, #f7f2e9)",
              border: "1px solid var(--t-brass, #8b6914)",
              borderRadius: 8, fontSize: 13.5,
              color: "var(--t-ink, #1a1614)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 14, flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 320px", lineHeight: 1.55 }}>
              <span
                className="t-mono uppercase"
                style={{
                  fontSize: 9.5, letterSpacing: "0.22em",
                  color: "var(--t-brass-deep, #7d5b21)",
                  display: "block", marginBottom: 4, fontWeight: 600,
                }}
              >Did you mean…</span>
              <span style={{ fontSize: 15 }}>
                <strong style={{ textTransform: "capitalize" }}>
                  {results.conflict_banner.trigger}
                </strong> in <strong>{results.conflict_banner.inferred_city}</strong>?
                <span style={{ color: "var(--t-ink3, #807366)", marginLeft: 8 }}>
                  Your <em>{results.conflict_banner.current_city_chip}</em> chip
                  is still selected.
                </span>
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                data-testid="enjoy-conflict-switch"
                onClick={() => {
                  setCity(results.conflict_banner.inferred_city);
                  setPage(1);
                }}
                className="enjoy-btn"
                style={{ fontSize: 12, padding: "8px 16px" }}
              >Switch to {results.conflict_banner.inferred_city} →</button>
              <button
                type="button"
                data-testid="enjoy-conflict-keep"
                onClick={() => {
                  // Hide the banner by clearing the query — keeps the
                  // user's chip selection intact.
                  setQuery("");
                  setPage(1);
                }}
                className="enjoy-btn ghost"
                style={{ fontSize: 12, padding: "8px 16px" }}
              >Keep {results.conflict_banner.current_city_chip}</button>
            </div>
          </div>
        )}

        {/* Place/attraction rail · founder-approved 18 Jul 2026. Hoisted
            ABOVE the grid branching so it survives the zero-result /
            relaxed-ladder / category-doors states — "amusement park in
            Mumbai" IS answerable even when no events match. */}
        {!loading && (
          <PlacesRail
            places={results?.places}
            onConcierge={(p) => handleRequestConcierge({
              source: "curated", event_id: "_generic", kind: "event",
              title: `Visit · ${p.name}`, category: activeCategoryTab,
              city: city || undefined, _is_generic: true,
            })}
          />
        )}

        {/* Grid · with booking-path filter applied client-side */}
        {(() => {
          // Resolve booking_path for an event:
          //   "direct"    · has any ladder/booking_url to TM/BMS/etc
          //   "onsite"    · only has venue/official-site link
          //   "concierge" · no machine route · desk-only
          const pathOf = (ev) => {
            if (ev.kind === "experience") return "concierge";
            const ladder = ev.booking?.ladder || [];
            const tier1Kinds = new Set([
              "ticketmaster", "ticketmaster_uk", "ticketmaster_us",
              "bookmyshow", "district", "platinumlist", "dice",
              "skiddle", "eventim",
            ]);
            if (ladder.some((r) => r && r.url && tier1Kinds.has(r.kind))) return "direct";
            if (ev.direct_book_url) return "direct";
            if (ladder.some((r) => r && r.url && (r.kind === "official" || r.kind === "venue"))) return "onsite";
            return "concierge";
          };
          const filteredEvents = bookingPaths.size === 0
            ? results.events
            : results.events.filter((e) => bookingPaths.has(pathOf(e)));

          if (loading) {
            return (
              <div
                data-testid="enjoy-loading"
                className="t-mono uppercase"
                style={{ padding: 80, textAlign: "center", color: "var(--t-ink3)",
                         fontSize: 11, letterSpacing: "0.18em" }}
              >Loading the latest…</div>
            );
          }
          // FIR-20260217-SEARCH · Layer 1 · truth-in-UI.
          // While the member is still typing (typing window 750ms), never
          // render the empty/did-you-mean/category-doors state — render
          // loading instead. The page does NOT say "couldn't find it"
          // mid-keystroke. Empty state fires only when the query has
          // genuinely settled.
          if (stillTyping && hasActiveQuery && filteredEvents.length === 0) {
            return (
              <div
                data-testid="enjoy-typing"
                className="t-mono uppercase"
                style={{ padding: 80, textAlign: "center", color: "var(--t-ink3)",
                         fontSize: 11, letterSpacing: "0.18em" }}
              >Listening…</div>
            );
          }
          if (filteredEvents.length === 0) {
            // Doctrine: when rails exist, NEVER render the dead empty-state.
            // Founder ruling 15 Feb 2026 PM-4 · category doors lead the
            // member-facing strip; platform-named rows retired.
            if (results.category_doors && results.category_doors.length > 0) {
              return (
                <CategoryDoors
                  doors={results.category_doors}
                  resolution={results.category_doors_resolution}
                  city={city || null}
                  countryNames={results.country_display_names}
                  onConciergeRequest={handleEmptyTellUs}
                />
              );
            }
            return (
              <>
                {/* ── Did you mean? · founder ruling 16 Feb 2026 PM-21 ─
                      When the exact query returned 0 results but TM
                      Suggest matched a close candidate via fuzzy
                      correction (`Ed Shereen` → `Ed Sheeran`), surface
                      a one-tap pivot. We never silently rewrite the
                      member's query — they confirm by tapping. */}
                {hasActiveQuery && results.did_you_mean && (
                  <div
                    data-testid="enjoy-did-you-mean"
                    style={{
                      marginTop: 32, marginBottom: 12,
                      padding: "16px 22px", borderRadius: 12,
                      background: "var(--t-brass-wash, rgba(160,122,56,0.10))",
                      border: "1px solid var(--t-brass2, #d6c388)",
                      display: "flex", alignItems: "center", gap: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      className="t-mono uppercase"
                      style={{
                        fontSize: 10, letterSpacing: "0.22em",
                        color: "var(--t-brass-deep, #7d5b21)",
                        fontWeight: 600,
                      }}
                    >Did you mean</span>
                    <button
                      type="button"
                      data-testid="enjoy-did-you-mean-pivot"
                      onClick={() => {
                        setQuery(results.did_you_mean);
                        setPage(1);
                      }}
                      className="t-serif"
                      style={{
                        background: "none", border: 0, padding: 0,
                        color: "var(--t-ink, #1a1614)",
                        fontSize: 18, fontWeight: 500, cursor: "pointer",
                        fontFamily: "Fraunces, serif",
                        textDecoration: "underline",
                        textDecorationColor: "var(--t-brass, #8b6914)",
                        textUnderlineOffset: 4,
                        textDecorationThickness: 2,
                      }}
                    >{results.did_you_mean}?</button>
                    <span
                      style={{
                        fontSize: 12.5, color: "var(--t-ink3, #807366)",
                        marginLeft: "auto",
                      }}
                    >We&rsquo;ll find the right door.</span>
                  </div>
                )}
                {/* ── No-result heading · founder ruling 16 Feb 2026 PM-19 ──
                      When a query returned zero matches, the page mode
                      MUST not fall back to generic Wimbledon / FIFA cards.
                      State the truth honestly and offer a clear next
                      step. */}
                {hasActiveQuery && (
                  <div
                    data-testid="enjoy-no-results-header"
                    style={{
                      marginTop: 32, marginBottom: 18,
                      padding: "20px 24px", borderRadius: 12,
                      background: "var(--t-paper2, #fbf6ec)",
                      border: "1px solid var(--t-line2, #d9cdb7)",
                    }}
                  >
                    <p
                      data-testid="enjoy-no-results-copy"
                      style={{
                        margin: 0, fontSize: 15, lineHeight: 1.5,
                        color: "var(--t-ink, #1a1614)",
                      }}
                    >
                      We couldn&rsquo;t find an exact match for{" "}
                      <strong style={{ color: "var(--t-ink)" }}>“{query.trim()}”</strong>,
                      but the Concierge® can route this.
                    </p>
                    <p
                      data-testid="enjoy-no-results-signature-note"
                      style={{
                        margin: "8px 0 0", fontSize: 13, lineHeight: 1.5,
                        color: "var(--t-ink3, #807366)", fontStyle: "italic",
                      }}
                    >
                      The Concierge® can also suggest private access or{" "}
                      <button
                        type="button"
                        onClick={() => navigate("/enjoy/signature")}
                        data-testid="enjoy-no-results-signature-link"
                        style={{
                          background: "none", border: 0, padding: 0,
                          color: "var(--t-brass, #8b6914)", cursor: "pointer",
                          fontSize: 13, textDecoration: "underline",
                          textUnderlineOffset: 3, fontStyle: "italic",
                        }}
                      >Signature Experiences</button>{" "}
                      around this request.
                    </p>
                    <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        data-testid="enjoy-no-results-ask"
                        onClick={handleEmptyTellUs}
                        className="enjoy-btn"
                        style={{ fontSize: 13, padding: "10px 18px" }}
                      >Ask the Concierge®</button>
                      <button
                        type="button"
                        data-testid="enjoy-no-results-clear"
                        onClick={() => { setQuery(""); setPage(1); }}
                        className="enjoy-btn ghost"
                        style={{ fontSize: 13, padding: "10px 18px" }}
                      >Clear search</button>
                    </div>
                  </div>
                )}
                <EmptyState
                  onTellUs={handleEmptyTellUs}
                  onQuickSend={handleEmptyQuickSend}
                  query={query}
                  city={city}
                  categoryLabel={activeCategoryTab}
                  member={member}
                />
                {/* ── Relaxed-fallback ladder · founder ruling 16 Feb PM-8 ──
                      When the committed filter set returns zero, the desk
                      relaxes filters (price → booking → date → category →
                      city) and shows the closest real matches here. No
                      generic overlays · only real verified rows. */}
                {relaxedResults && relaxedResults.events && relaxedResults.events.length > 0 && (
                  <section
                    data-testid="enjoy-closest-matches"
                    style={{ marginTop: 32 }}
                  >
                    <div
                      className="t-mono uppercase"
                      style={{
                        fontSize: 11, letterSpacing: "0.22em",
                        color: "var(--t-ink2)", marginBottom: 14,
                      }}
                    >
                      Closest matches we can route{relaxedReason ? ` · relaxed ${relaxedReason}` : ""}
                    </div>
                    <div
                      style={{
                        display: "grid", gap: 24,
                        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                      }}
                    >
                      {relaxedResults.events.slice(0, 6).map((ev) => (
                        <EventCard
                          key={`relaxed-${ev.source}-${ev.event_id}`}
                          event={ev}
                          onSelect={setActiveEvent}
                          countryNames={relaxedResults.country_display_names}
                        />
                      ))}
                    </div>
                  </section>
                )}
              </>
            );
          }
          return (
            <>
              {/* ── Results header · founder ruling 16 Feb 2026 PM-19 ─────
                    When a query is active, prefix the grid with an
                    explicit "Results for 'X'" headline. This makes the
                    page mode obvious and visually separates real search
                    output from generic discovery content. */}
              {hasActiveQuery && (
                <div
                  data-testid="enjoy-results-header"
                  style={{
                    marginTop: 32, marginBottom: 8,
                    display: "flex", alignItems: "baseline",
                    justifyContent: "space-between",
                    flexWrap: "wrap", gap: 12,
                  }}
                >
                  <h2
                    className="t-serif"
                    style={{
                      fontFamily: "Fraunces, serif",
                      fontSize: 24, fontWeight: 500,
                      color: "var(--t-ink, #1a1614)", margin: 0,
                    }}
                  >
                    Results for <span style={{ color: "var(--t-brass, #8b6914)", fontStyle: "italic" }}>“{query.trim()}”</span>
                  </h2>
                  <button
                    type="button"
                    data-testid="enjoy-clear-search"
                    onClick={() => { setQuery(""); setPage(1); }}
                    className="t-mono uppercase enjoy-btn ghost"
                    style={{ fontSize: 11, letterSpacing: "0.16em", padding: "6px 14px" }}
                  >Clear search</button>
                </div>
              )}
              {(() => {
                // Split events into Official routes (TM / direct ticketing /
                // verified provider) vs Concierge® suggestions (curated +
                // signature). Founder ruling 16 Feb 2026 PM-19: search
                // ranking priority is exact artist/event/venue match →
                // official ticketing → relevant Signature. The split
                // headings make that hierarchy visible.
                const officialRoutes = filteredEvents.filter(
                  (e) => !e.is_curated && !e.is_signature
                );
                const conciergeSuggestions = filteredEvents.filter(
                  (e) => e.is_curated || e.is_signature
                );
                const showSplit = hasActiveQuery && (
                  officialRoutes.length > 0 && conciergeSuggestions.length > 0
                );
                if (showSplit) {
                  return (
                    <>
                      <div
                        data-testid="enjoy-section-label-official"
                        className="t-mono uppercase"
                        style={{
                          marginTop: 18, marginBottom: 6,
                          fontSize: 11, letterSpacing: "0.22em",
                          color: "var(--t-ink2, #4f463c)", fontWeight: 600,
                        }}
                      >Official routes</div>
                      <div
                        data-testid="enjoy-grid"
                        style={{
                          display: "grid", gap: 24, marginTop: 10,
                          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                        }}
                      >
                        {officialRoutes.map((e, i) => (
                          <EventCard key={`${e.source}-${e.event_id}`} event={e} index={i} onSelect={setActiveEvent} />
                        ))}
                      </div>
                      <div
                        data-testid="enjoy-section-label-concierge"
                        className="t-mono uppercase"
                        style={{
                          marginTop: 36, marginBottom: 6,
                          fontSize: 11, letterSpacing: "0.22em",
                          color: "var(--t-brass, #8b6914)", fontWeight: 600,
                        }}
                      >Concierge® suggestions</div>
                      <div
                        data-testid="enjoy-grid-concierge"
                        style={{
                          display: "grid", gap: 24, marginTop: 10,
                          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                        }}
                      >
                        {conciergeSuggestions.map((e, i) => (
                          <EventCard key={`${e.source}-${e.event_id}`} event={e} index={i} onSelect={setActiveEvent} />
                        ))}
                      </div>
                    </>
                  );
                }
                // Default: single grid (landing or query with only one type
                // of result · no split needed).
                return (
                  <div
                    data-testid="enjoy-grid"
                    style={{
                      display: "grid", gap: 24, marginTop: hasActiveQuery ? 10 : 28,
                      gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                    }}
                  >
                    {filteredEvents.map((e, i) => (
                      <EventCard
                        key={`${e.source}-${e.event_id}`}
                        event={e}
                        index={i}
                        onSelect={setActiveEvent}
                      />
                    ))}
                  </div>
                );
              })()}
              {results.category_doors && results.category_doors.length > 0 && (
                <CategoryDoors
                  doors={results.category_doors}
                  resolution={results.category_doors_resolution}
                  city={city || null}
                  countryNames={results.country_display_names}
                  onConciergeRequest={handleEmptyTellUs}
                />
              )}
              {results.total > filteredEvents.length && bookingPaths.size === 0 && (
                <div style={{ display: "flex", justifyContent: "center", gap: 12, padding: "36px 0" }}>
                  <button className="enjoy-btn ghost" disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="enjoy-pager-prev">
                    ← Previous
                  </button>
                  <span className="t-mono"
                    style={{ alignSelf: "center", color: "var(--t-ink3)", fontSize: 12, letterSpacing: "0.14em" }}>
                    Page {page}
                  </span>
                  <button className="enjoy-btn ghost"
                    disabled={filteredEvents.length < 24}
                    onClick={() => setPage((p) => p + 1)} data-testid="enjoy-pager-next">
                    Next →
                  </button>
                </div>
              )}
            </>
          );
        })()}

        {/* Main cities rail · sits below all curated/featured rails,
            above the footer. Click → pre-fills the search and runs
            it (Enjoy debounces · `setQuery` alone triggers the
            search after ~300ms). */}
        <TopCitiesGrid
          pillar="enjoy"
          picks={PICKS_ENJOY}
          onPick={(query) => {
            setQuery(query);
            setPage(1);
            try {
              window.scrollTo({ top: 0, behavior: "smooth" });
            } catch { /* older browsers · ignore */ }
          }}
          eyebrow="Main cities"
          title="Cities the calendar knows"
        />
      </main>

      <TableFooter slug="enjoy" tagline="The Concierge® holds the door." />

      {activeEvent && !requestingEvent && (
        <EventDrawer
          event={activeEvent}
          onClose={() => setActiveEvent(null)}
          onBookDirect={handleBookDirect}
          onRequestConcierge={handleRequestConcierge}
          isAdminViewer={Boolean(member && (member.role === "admin" || member.role === "desk" || member.is_admin))}
        />
      )}
      {requestingEvent && (
        <RequestModal
          event={requestingEvent}
          member={member}
          onClose={() => setRequestingEvent(null)}
          onSubmitted={handleRequestSubmitted}
        />
      )}

      <TableFooter slug="enjoy" tagline="The Concierge® holds the door." />

      {activeEvent && !requestingEvent && (
        <EventDrawer
          event={activeEvent}
          onClose={() => setActiveEvent(null)}
          onBookDirect={handleBookDirect}
          onRequestConcierge={handleRequestConcierge}
          isAdminViewer={Boolean(member && (member.role === "admin" || member.role === "desk" || member.is_admin))}
        />
      )}
      {requestingEvent && (
        <RequestModal
          event={requestingEvent}
          member={member}
          onClose={() => setRequestingEvent(null)}
          onSubmitted={handleRequestSubmitted}
        />
      )}
      {/* Refine drawer · staged filters · Apply/Clear/Cancel · founder 16 Feb PM-8 */}
      <RefineDrawer
        open={refineOpen}
        pending={pendingFilters}
        setPending={setPendingFilters}
        onApply={applyRefine}
        onClearAll={clearAllRefine}
        onCancel={() => setRefineOpen(false)}
      />
      {/* Persistent Talk-to-Concierge pill · mirror /table.
          Hidden when the Refine drawer is open so it never overlaps the
          drawer's Apply / Clear / Cancel controls. */}
      {!refineOpen && <TableWhatsAppPill context="enjoy" />}
    </div>
  );
}
